import { Suspense, lazy, useState, type ReactNode } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { ROL_ETIKET, kesimRotasiMi, yetkiMetni, yetkiliMi, type AyniTamirKanit, type AyniTamirOnerisi, type KesimGirdisiYaniti, type KesimPlaniGovdesi, type OlasiEslesmeGerekcesi, type PaketSecenegi, type StokPnCifti } from "@oasis/domain";
import { GenisModal, KesimPlaniAdimi, veIle, type KesimIsi } from "@/components/kesim-plani-penceresi";
import { api } from "@/lib/api";
import { useVeri, dosyaSec, dosyaYukle, tarih, tarihSaat } from "@/lib/veri";
import { Band, Bos, Buton, Cip, Etiket, Hata, Kutu, Kv, Modal, Sayfa, alan, etiket as etiketSinifi, girdi, tabloBas, tabloHucre } from "@/components/parcalar";
import { useOturum } from "@/lib/oturum";
import { cevir } from "@/lib/dil";
import { idariIlkTip, idariKaydedilebilir, idariSaveIpucu } from "@/lib/idari-aksiyon";
import { olasiGerekceMetni, paketDegisimListesi, paketEtiketi, stoktanListesi } from "@/lib/stok-paket";
import {
  ekAcikSatirlar, ekAcilmamisCumlesi, ekAlintisi, ekBantAciklamasi, ekBantBasligi, ekBantKuyrukCumlesi, ekCubukAlti, ekKalemEtiketi, ekPencereAktarilmisCumleleri,
  ekPencereKuyrukCumlesi, ekSatirAdi, ekSatirCipi, ekSonucSatirlari, ekSonucTonu, ekYenidenAcikCumlesi, kararTonu,
  type EkKuyruk, type EkRejectYaniti,
} from "@/lib/ek-reject";
import { BOS_SAYFA, SayfaAlani, belgeLinki, pdfSayfasi, sayfaAlanlari, sayfaEtiketi, sayfaGecerli, type SayfaAraligi, type SayfaSecimi } from "@/components/pdf-sayfa";
import {
  BekleyenKalemSagi, BeyanliKalemSagi, KayitYokDamgasiKutusu, KayitYokGeriPenceresi, KayitYokPenceresi, kayitYokAktif, kayitYokBantEki, kayitYokCubukAlti,
  kayitYokGeriNotlari, kayitYokGrupBasligi, kayitYokGrupNotu, komponentRozeti, muayeneKalemleri, muayeneYenidenAcikCumlesi, type KomponentRozeti,
} from "@/components/muayene-kayit-yok";
/** pdf.js ~350 kB; yalnız bir sayfa referansına tıklandığında indirilir. */
const SurvrepGoruntuleyici = lazy(() => import("@/components/survrep").then((m) => ({ default: m.SurvrepGoruntuleyici })));

/**
 * Muayene doğrulama triyajı, kalem düzeyi (E-15 v4).
 *
 * Kurgu birimi KALEM değil KOMPONENTtir: hedef her komponentin barının tamamen yeşile dönmesidir.
 * İş kuralları sunucuda uygulanır (muayene.controller.ts); burası onları gösterir.
 * Otomatik mail yoktur: kullanıcı kendi mailini atar, sistem yalnız sipariş tarihini ve değişimi loglar.
 */

interface SayfaRef { etiket: string; sayfa: number }
interface Kalem {
  id: string; no: number; kalem: string; snid: string | null; sonuc: string | null; bulgu: string | null; karar: string | null;
  onerilenIs: string | null; kapsam: string | null; goeKapsam: string | null; stOneri: string | null; rotaTipi: string | null;
  sinif: string; siparisPn: string | null; mrbSectionlar: string | null; mrbKimlikler: string | null; celiski: string | null;
  sayfa: string | null; sayfaRefleri: SayfaRef[]; durum: string; rotaSecim: string | null; yeniId: string | null;
  /// 2. durum: rework geçiş verisi (değişmez) + idari kayıt (kullanıcı aksiyonu)
  kapanis: { item: string | null; sonuc: string | null; minwt: string | null; ekstra: string | null; dispoAd: string | null; not: string | null; dispo: string | null; dispoTarih: string | null } | null;
  rworkRefleri: SayfaRef[]; eksik: boolean; eksikNot: string | null; kaynakNot: string | null; teyit: string | null;
  idariTip: string | null; idariGerekce: string | null; idariKanit: string | null; idariYapan: string | null; idariTarih: string | null;
  /// D-039: bulgunun bağlı olduğu iş (N bulgu → 1 iş) ve rework girdisinin rota metni; `oneri` sunucuda @oasis/domain ile hesaplanır.
  tamirId: string | null; rotaNotu: string | null; oneri: AyniTamirOnerisi | null;
  /// D-040: son geri gönderme notu; `geriIsIptal` = geri gönderildiği iş iptal edildi (son bulgusu gitti).
  geriTs: string | null; geriKim: string | null; geriGerekce: string | null; geriTamirId: string | null; geriIsIptal: boolean;
  /// D-041: değişimde seçilen stok kaydı (PN) ve seçim anındaki tanımın kopyası. D-050: PN paketin PN'idir.
  stokPn: string | null; stokTanim: string | null;
  /// D-050: kalemin etkin stok paketi seçimi (stok_kullanim, hedef "triaj"); paketsiz eski seçimde (D-041) null.
  stokPaket: { id: string; no: number; pkg: string; pn: string; bitti: boolean } | null;
  /// D-069: kalem rework raporunun REJECT'li ek doğrulama satırından açıldı (numarası 9001'den başlar); `ekItem` satırın "Rework item" hücresi.
  ekDogrulama: boolean; ekItem: string | null;
  /**
   * D-072: muayene bekleyen kalemdeki "No record available"ın damgası ve geri alınanlar (`muayene_kalem.kayit_yok`, JSON, sunucudan
   * olduğu gibi; `kayitYokAktif` toleranslı okur). Beyanlı kalemde `durum` "kayit_yok"tur. Eski yanıtta alan yok.
   */
  kayitYok?: unknown;
}
/**
 * İş özeti (sunucu: tamir.service.ts `IsOzeti`). İptal edilen iş hiç gelmez; rotası biten iş gelir (`bitti`).
 * `bulgular` öncü bulgu önde, sonra numara sırasında.
 */
export type IsOzeti = {
  id: string; riserRn: string; hat: string; poz: string | null; is: string; rotaKod: string;
  adim: number; adimSayisi: number; adimAd: string | null; bitti: boolean; liderNo: number | null;
  bulgular: { no: number; kalem: string; komponentId: string }[];
  kayitYokAdimlar: string[];
};
/** D-039 Waiting Items 1. adımında kalemin kararı: ayrı iş, bir gruba katıl (öncü no) ya da süren işe bağlan. */
type Birlesim = { tur: "ayri" } | { tur: "grup"; lider: number } | { tur: "is"; tamirId: string };
/** D-069: `reject` = Result REJECT diyor (sunucu hesaplar); `kalemId` = satırın açıldığı değişim kalemi (açılmadıysa null). */
interface EkDogrulama { id: string; sira: number; item: string | null; ad: string; sonuc: string | null; kanit: string | null; rworkRefleri: SayfaRef[]; kalemId: string | null; reject: boolean }
interface Komponent {
  id: string; kod: string; ad: string; sira: number; gorunur: boolean;
  mrbSection: string | null; mrbSatirAdi: string | null; mrbPn: string | null; mrbKimlik: string | null; baslikNotu: string | null;
  siparisTarihi: string | null; aktarildi: boolean; gecenAktarildi: boolean;
  kalemler: Kalem[]; loglar: { id: string; ts: string; kim: string; mesaj: string }[]; ekDogrulamalar: EkDogrulama[];
  /** D-039: bu komponentin bulgularını taşıyan işler (iptal edilenler hariç, rotası bitenler dahil). */
  isler: IsOzeti[];
  bar: { yesil: number; sari: number; kirmizi: number; gri: number; toplam: number };
  tamam: boolean; aktarilabilir: boolean;
  /**
   * D-069: `ek` yalnız yeşil sayılan ek satırlar; `ekReject` kalemi açılmamış REJECT satırları; `ekBekleyen` o satırlardan açılmış bekleyen kalemler.
   * D-072: `muayeneBekleyen` beyansız muayene bekleyen kalemler; `muayeneKayitYok` "No record available" yazılmış olanlar (yeşil).
   * D-075: `gecenBekliyor` Fastener'ın aktarılmayı bekleyen geçen kalemleri (öteki komponentlerde 0); rozet ve nokta `tamam` ile birlikte okur.
   */
  sayim: { gecen: number; rework: number; degisim: number; bekleyen: number; muayeneBekleyen: number; muayeneKayitYok: number; reworkKapanan: number; reworkAcik: number; ek: number; ekReject: number; ekBekleyen: number; idari: number; dogrulanmayan: number; gecenBekliyor: number };
}
/** D-047: `aktif` false = kütükte pasif ST (yeni seçimde sunulmaz; eski fikstürlerde alan yok, aktif sayılır). */
interface Stg { kod: string; ad: string; rotaKod: string; malzeme: boolean; tip: string; aktif?: boolean }
/** D-065: girdiyi teslim eden: kişi (`ad` yükleyen) ya da dönüştürücü (`ad` anahtarın adı, `ts` teslim zamanı). */
interface GirdiKaynagi { kaynak: "elle" | "donusturucu"; ad: string | null; ts: string | null }
/** "converter · {anahtar adı} · {zaman}" ya da "by hand · {ad}". */
const kaynakMetni = (k: GirdiKaynagi) => (k.kaynak === "donusturucu" ? [cevir("converter"), k.ad, k.ts ? tarihSaat(k.ts) : null] : [cevir("by hand"), k.ad]).filter(Boolean).join(" · ");
interface Detay {
  riser: { rn: string; riserAd: string | null; assemblySn: string | null; assemblyPnMrb: string | null; assemblyPnSurvrep: string | null; rapor: string | null; musteriPo: string | null; prosedurler: string | null; mufettisler: string | null; dispoImza: string | null; dispoTarihi: string | null; kaynak: string | null; survrepDokumanId: string | null; survrepAd: string | null; survrepSayfa: number | null; dosya: string; yukleyen: string; ts: string; kaynakMrbIcerik: string | null; kaynakKararDb: string | null; mrbKaydi: { id: string; sn: string; icerikVar: boolean } | null;
    reworkIslendi: boolean; reworkRapor: string | null; reworkDosya: string | null; reworkDokumanId: string | null; reworkSayfaAraligi: SayfaAraligi | null; survrepSayfaAraligi?: SayfaAraligi | null; reworkSayfa: number | null;
    reworkKaynakEki: string | null; reworkDogrulamaImza: string | null; reworkDogrulamaTarih: string | null; reworkDispoImza: string | null; reworkDispoTarih: string | null;
    reworkTs: string | null; reworkYukleyen: string | null };
  /** D-065: girdinin kaynağı (elle ya da dönüştürücü); rework işlenmediyse `reworkGirdiKaynagi` null. Yanıtın üst düzeyindedir. */
  girdiKaynagi?: GirdiKaynagi | null;
  reworkGirdiKaynagi?: GirdiKaynagi | null;
  stg: Stg[];
  /** D-050: "From stock" seçiminin listesi: aktif stok paketleri (PN sırası, aynı PN'de önce açılış paketi, sonra geliş sırası). */
  paketler: PaketSecenegi[];
  /** D-046: biten paketler; yalnız kapanmış kalemin "Change stock package" listesinde en altta. */
  bitenPaketler: PaketSecenegi[];
  /**
   * D-076 (c): bu riserin Order PN'lerine ait çiftler (sunucu bütün riserların değişim kalemlerinden kurar); olası eşleşme
   * (`stokSecenekleri`) bunlarla hesaplanır. Eski yanıtta alan yok.
   */
  stokPnCiftleri?: StokPnCifti[];
  esleme: { id: string; sira: number; tur: string; mesaj: string }[];
  /** D-069: riser assembly kuyruğundaysa gönderen, zaman ve Coating / Assembly raporunun yüklü olup olmadığı; kuyrukta değilse null. */
  kuyruk: EkKuyruk | null;
  komponentler: Komponent[];
}

const IDARI_AD: Record<string, string> = { "osi-iade": cevir("Return to OSI, additional verification request"), kanit: cevir("closed with evidence (Accept)"), kabul: cevir("Accept as-is"), reject: cevir("Converted to Reject") };

const GUN = 86_400_000;
const gunFark = (iso: string | null) => (iso ? Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / GUN)) : null);

/** İş kimliğinin kısa biçimi (log satırlarındaki "task 05d8b642" ile aynı). */
export const isKisa = (id: string) => id.slice(0, 8);
/** Adım gösterimi uygulamanın biçimiyle: "step 3/10 NDT: MT (bevel)". Adım adı sunucu verisi, çevrilmez. */
export const adimMetni = (j: { adim: number; adimSayisi: number; adimAd: string | null; bitti: boolean }) =>
  j.adim < 0 ? cevir("waiting for material") : j.bitti ? cevir("route completed") : cevir`step ${j.adim + 1}/${j.adimSayisi} ${j.adimAd ?? ""}`;
/** D-039 kanıt satırı: yedek kural · rework girdisi cümlesi · sipariş PN notu (son ikisi birebir). */
export function ayniTamirKanitMetni(k: AyniTamirKanit, no: number): string {
  const p: string[] = [];
  if (k.yedek) {
    const s = "rw " + k.yedek.sayfalar.map((x) => `s.${x}`).join(", ");
    p.push(k.yedek.sayfalar.length > 1
      ? cevir`Matched on: same component, same ST (${k.yedek.st}), same rework pages (${s})`
      : cevir`Matched on: same component, same ST (${k.yedek.st}), same rework page (${s})`);
  }
  if (k.reworkMetni) p.push(cevir`Rework input: “${k.reworkMetni}”`);
  if (k.siparisNotu) p.push(cevir`order PN of #${no}: “${k.siparisNotu}”`);
  return p.join(" · ");
}
/**
 * D-042: aynı tamir önerisine kişinin cevabı; kaydı olmayan kalemin önerisi açıktır ("acik"). Pencere açılırken
 * hiçbir öneri uygulanmaz: "Send as one job" / "Attach to running job" kabul eder, "Keep separate" reddeder.
 */
type OneriCevabi = "kabul" | "red";
/** Cümle parçalarını birleştirir; noktasız biten parçaya nokta ekler. */
const cumleler = (p: string[]) => p.map((s) => (/[.!?]$/.test(s) ? s : s + ".")).join(" ");

export function DogrulamaRiser({ rn }: { rn: string }) {
  const { veri, hata, yenile } = useVeri<Detay>(`/muayene/${rn}`);
  const nav = useNavigate();
  const { ben } = useOturum();
  const [islem, setIslem] = useState<string | null>(null);
  const [acik, setAcik] = useState<Record<string, boolean>>({});
  const [siparis, setSiparis] = useState<Komponent | null>(null);
  const [siparisTarih, setSiparisTarih] = useState("");
  const [degisim, setDegisim] = useState<Komponent | null>(null);
  /** Waiting Items: işaret, yeni ürün ID ve D-050 stok paketi seçimi (paket id; "" = stoktan değil). */
  const [secim, setSecim] = useState<Record<number, { isaretli: boolean; yeniId: string; stokPaketId: string }>>({});
  /** D-076: Waiting Items'ta kalem başına "Show all stock packages" kutusu; kaydedilmez, pencere her açılışta işaretsiz gelir. */
  const [tumPaketler, setTumPaketler] = useState<Record<number, boolean>>({});
  /** D-039: Waiting Items penceresinde tamir kalemlerinin gruplama / bağlama kararı (işaretten bağımsız). */
  const [birlesim, setBirlesim] = useState<Record<number, Birlesim>>({});
  /** D-042: önerisi olan bulgunun cevabı (anahtar bulgu no; kaydı yoksa öneri açık). */
  const [oneriCevap, setOneriCevap] = useState<Record<number, OneriCevabi>>({});
  /**
   * D-035: Waiting Items penceresinin adımı. 2. adım "Cut plan approval": kesim rotalı işaretli iş başına bir kesim
   * tablosu (grup tek iş). Pencere her açılışta 1. adımdan başlar; "Cancel" taslak tutmaz.
   */
  const [kesimAdim, setKesimAdim] = useState<1 | 2>(1);
  const [kesimIsleri, setKesimIsleri] = useState<KesimIsi[]>([]);
  const [kesimYukleniyor, setKesimYukleniyor] = useState(false);
  const [gonderiliyor, setGonderiliyor] = useState(false);
  // Çift kaynak: mavi s.N Conventional, mor rw s.N Rework Scope (07-md §3.3).
  const [pdf, setPdf] = useState<{ dokumanId: string; ad: string | null; sayfa: number } | null>(null);
  const [sifirla, setSifirla] = useState(false);
  const [sifirlaniyor, setSifirlaniyor] = useState(false);
  const [idari, setIdari] = useState<{ kod: string; no: number; kalem: string; mevcut: string | null } | null>(null);
  // D-078: boş = "Select..." (aksiyon seçilmedi); açılıştaki değeri `idariIlkTip` verir.
  const [idariTip, setIdariTip] = useState("");
  const [idariGerekce, setIdariGerekce] = useState("");
  const [idariKanit, setIdariKanit] = useState("");
  const [idariSt, setIdariSt] = useState("");
  const [reworkAcik, setReworkAcik] = useState(false);
  const [reworkPdf, setReworkPdf] = useState<File | null>(null);
  // D-034: Rework Scope PDF'i birleştirilmiş bir PDF'in yalnız bazı sayfalarıysa "Partial" + sayfa.
  const [reworkSayfaSecimi, setReworkSayfaSecimi] = useState<SayfaSecimi>(BOS_SAYFA);
  const [reworkGecis, setReworkGecis] = useState<File | null>(null);
  const [reworkYukleniyor, setReworkYukleniyor] = useState(false);
  const [reworkSonuc, setReworkSonuc] = useState<string[] | null>(null);
  /**
   * D-050: kapanmış kalemde açık "Change stock package" listesi (kalem id; değer paket id, "" stoktan değil, "mevcut" yer tutucu).
   * D-076: `hepsi` "Show all stock packages" kutusudur; kaydedilmez, liste her açılışta işaretsiz gelir.
   */
  const [paketDegisim, setPaketDegisim] = useState<{ id: string; deger: string; hepsi: boolean } | null>(null);
  /** D-069: "Open additional REJECT rows as Items" onay penceresi ve süren isteği. */
  const [ekAc, setEkAc] = useState(false);
  const [ekAciliyor, setEkAciliyor] = useState(false);
  /** D-072: muayene bekleyen kalemin "No record available…" penceresi: hedef kalem, yazılan gerekçe ve kanıt, süren istek. */
  const [kayitYok, setKayitYok] = useState<{ kod: string; komponent: string; no: number; kalem: string; gerekce: string; kanit: string; mesgul: boolean } | null>(null);
  /** D-072: beyanlı kalemin "Take back…" penceresi: hedef kalem (damga ve sarı kutu taze veriden okunur) ve süren istek. */
  const [kayitYokGeri, setKayitYokGeri] = useState<{ kod: string; no: number; mesgul: boolean } | null>(null);

  const dene = async (f: () => Promise<unknown>) => {
    setIslem(null);
    try { await f(); await yenile(); return true; } catch (e) { setIslem(e instanceof Error ? e.message : cevir("Error")); return false; }
  };

  if (hata) return <Hata mesaj={hata} />;
  if (!veri) return <Bos>{cevir("Loading...")}</Bos>;
  const { riser, stg } = veri;
  const gorunur = veri.komponentler.filter((k) => k.gorunur);
  const dispoGun = gunFark(riser.dispoTarihi);
  const celiskiler = veri.esleme.filter((e) => e.tur === "CELISKI");
  // Rework geçişinde eksik kalan item gibi "uygulandı ama tamamlanmadı" notları burada durur.
  const uyarilar = veri.esleme.filter((e) => e.tur === "UYARI");
  const toplamKalem = veri.komponentler.reduce((t, k) => t + k.kalemler.length, 0);
  const toplamLog = veri.komponentler.reduce((t, k) => t + k.loglar.length, 0);
  /** U-040: "Reset set"i engelleyen işler: setin bulgusunu taşıyan, iptal edilmemiş işler (bitenler dahil), birer kez. */
  const sifirlamaIsleri = [...new Map(veri.komponentler.flatMap((k) => k.isler).map((j) => [j.id, j] as const)).values()];
  /** İşin bulguları komponent adıyla, öncü önce: "Main Tube #8, #9". */
  const bulguMetni = (j: IsOzeti) => {
    const komponentBasina = new Map<string, number[]>();
    for (const b of j.bulgular) {
      const ad = veri.komponentler.find((k) => k.id === b.komponentId)?.ad ?? "";
      komponentBasina.set(ad, [...(komponentBasina.get(ad) ?? []), b.no]);
    }
    return [...komponentBasina].map(([ad, nolar]) => `${ad} ${nolar.map((n) => `#${n}`).join(", ")}`.trim()).join(", ");
  };
  const idariSayisi = veri.komponentler.reduce((t, k) => t + k.kalemler.filter((x) => x.idariTip).length, 0);
  /**
   * D-069: görünür komponentlerin kalemi açılmamış REJECT'li ek doğrulama satırları. Biri bile varken riser kutusunda kırmızı bant ve
   * "Open as Replacement Items (n)" düğmesi çizilir; bu değişiklikten önce yüklenmiş riserlerde satırlar o düğmeyle kaleme açılır.
   */
  const ekAcik = ekAcikSatirlar(gorunur);
  const ekKuyrukCumlesi = ekBantKuyrukCumlesi(riser.rn, veri.kuyruk, tarih);
  /** Onay penceresinin sarı kutusu: aktarılmış komponent başına bir cümle, riser kuyruktaysa kuyruk cümlesi. */
  const ekPencereNotlari = [...ekPencereAktarilmisCumleleri(ekAcik), ekPencereKuyrukCumlesi(riser.rn, veri.kuyruk)].filter((c): c is string => Boolean(c));
  /**
   * "Open as Items": tek istek (`POST /muayene/:rn/ek-reject`, gövdesiz, yetki "dogrula"). Sunucu kalemleri açar, log ve olay satırlarını
   * yazar, riser assembly kuyruğundaysa kuyruktan çıkarır. Sonuç sayfanın başındaki yeşil banda yazılır: açılan her kalem için bir satır.
   */
  const ekRejectAc = async () => {
    const sonuc: { yanit: EkRejectYaniti | null } = { yanit: null };
    setEkAciliyor(true);
    const tamam = await dene(async () => { sonuc.yanit = await api.post<EkRejectYaniti>(`/muayene/${rn}/ek-reject`); });
    setEkAciliyor(false);
    if (!tamam) return;
    const satirlar = ekSonucSatirlari(sonuc.yanit?.acilan ?? []);
    setReworkSonuc(satirlar.length ? satirlar : null);
    setEkAc(false);
  };

  /**
   * D-072 "Mark “No record available”": tek istek (`POST /muayene/:rn/kayit-yok`, yetki "kayitYokKapi"). Sunucu kalemi beyanlı yapar,
   * damgayı, log ve olay satırlarını yazar, sonra kuyruk denetimini çalıştırır. Başarıda sayfa yeniden okunur (bar, rozet, düğmeler,
   * kuyruk ve log satırları) ve pencere kapanır; hatada pencere sunucunun cümlesiyle açık kalır.
   */
  const kayitYokKaydet = async () => {
    if (!kayitYok) return;
    const { kod, no, gerekce, kanit } = kayitYok;
    setKayitYok((s) => s && { ...s, mesgul: true });
    const tamam = await dene(() => api.post(`/muayene/${rn}/kayit-yok`, { kod, no, gerekce, kanit: kanit.trim() || null }));
    setKayitYok((s) => (tamam ? null : s && { ...s, mesgul: false }));
  };
  /**
   * D-072 "Take back “No record available”": `POST /muayene/:rn/kayit-yok/geri-al` (yetki "kayitYokKapi"). Pencerenin gösterdiği
   * damganın zamanı gönderilir (bayat sayfa koruması; okunur damga yoksa gönderilmez). Kuyruk sonucu log satırlarına yazılır.
   */
  const kayitYokGeriAl = async (tarih: string | undefined) => {
    if (!kayitYokGeri) return;
    const { kod, no } = kayitYokGeri;
    setKayitYokGeri((s) => s && { ...s, mesgul: true });
    const tamam = await dene(() => api.post(`/muayene/${rn}/kayit-yok/geri-al`, { kod, no, ...(tarih ? { tarih } : {}) }));
    setKayitYokGeri((s) => (tamam ? null : s && { ...s, mesgul: false }));
  };
  /** D-072: "Take back…" penceresinin komponenti ve kalemi, taze veriden; kalem artık beyanlı değilse pencere çizilmez. */
  const geriKomponent = kayitYokGeri ? veri.komponentler.find((k) => k.kod === kayitYokGeri.kod) ?? null : null;
  const geriKalem = kayitYokGeri && geriKomponent ? muayeneKalemleri(geriKomponent.kalemler).beyanli.find((x) => x.no === kayitYokGeri.no) ?? null : null;

  /** Sayfa referansı çipleri; mavi Conventional, mor Rework Scope. Belge yoksa tıklanamaz gri kalır. */
  /** D-034: belge birleşik bir PDF'in parçasıysa (Partial) kalemin "p.N"si aralığın başına göre kaydırılır. */
  const refler = (liste: SayfaRef[], dokumanId: string | null, ad: string | null, mor: boolean, aralik?: SayfaAraligi | null) =>
    liste.length ? (
      <span className="ml-1 whitespace-nowrap">{liste.map((r) => (
        dokumanId
          ? <button key={r.etiket} type="button" className={`mr-1 rounded px-1.5 py-0.5 text-xs font-medium hover:opacity-80 ${mor ? "bg-hold-bg text-hold" : "bg-info-bg text-info"}`} onClick={() => setPdf({ dokumanId, ad, sayfa: pdfSayfasi(r.sayfa, aralik) })}>{r.etiket}</button>
          : <span key={r.etiket} className="mr-1 rounded bg-surface-2 px-1.5 py-0.5 text-xs text-ink-3" title={mor ? cevir("Rework Scope PDF is not in system") : cevir("PDF to show the page is not in system — chips become clickable when SURVREP or Disposition report of riser is uploaded")}>{r.etiket}</span>
      ))}</span>
    ) : null;
  const sref = (k: Kalem) => (
    <>
      {refler(k.sayfaRefleri, riser.survrepDokumanId, riser.survrepAd, false, riser.survrepSayfaAraligi)}
      {refler(k.rworkRefleri ?? [], riser.reworkDokumanId, riser.reworkRapor, true, riser.reworkSayfaAraligi)}
    </>
  );

  /** Kimlik çelişkisi ayrı kolonda gelir; kimliğe uyarı işaretini sayfa ekler (06-md §7). */
  const kimlikler = (k: Kalem) => {
    const satirlar = (k.mrbKimlikler ?? "").split("||").map((s) => s.trim()).filter(Boolean);
    if (!satirlar.length) return null;
    return (
      <div className="mt-1 space-y-0.5">
        {satirlar.map((s, i) => <div key={i} className={`text-xs ${k.celiski ? "text-bad" : "text-ink-3"}`}>{k.celiski ? "⚠ " : ""}{s}</div>)}
        {k.celiski && <div className="text-xs font-medium text-bad">{k.celiski}</div>}
      </div>
    );
  };

  /**
   * Komponent rozeti: dallar ve sıraları (D-069'un ek REJECT dalları önce, D-072 tasarımının 3.1.6'sı) `komponentRozeti`ndedir
   * (components/muayene-kayit-yok.tsx; U-069 inceleme turunda sayfadan oraya, testli saf fonksiyon olarak taşındı). Sayfa yalnız
   * sipariş tarihinin ekini kendi tarih yardımcılarıyla kurar.
   */
  const rozetBilgi = (k: Komponent): KomponentRozeti => komponentRozeti(k, k.siparisTarihi ? cevir` · order: ${tarih(k.siparisTarihi)} (${gunFark(k.siparisTarihi)} g)` : "");
  const rozet = (k: Komponent) => { const r = rozetBilgi(k); return <Etiket tone={r.ton}>{r.metin}</Etiket>; };
  const NOKTA = { ok: "bg-ok", warn: "bg-warn", bad: "bg-bad" } as const;

  const bar = (b: Komponent["bar"]) => {
    const p = (n: number) => (b.toplam ? `${(n / b.toplam) * 100}%` : "0%");
    return (
      <div className="mt-2 flex h-2 w-full overflow-hidden rounded-full bg-surface-2" title={cevir`${b.yesil} done · ${b.sari} rework · ${b.kirmizi} waiting · ${b.gri} inspection pending`}>
        <div className="bg-ok" style={{ width: p(b.yesil) }} />
        <div className="bg-warn" style={{ width: p(b.sari) }} />
        <div className="bg-bad" style={{ width: p(b.kirmizi) }} />
        <div className="bg-hold" style={{ width: p(b.gri) }} />
      </div>
    );
  };

  /**
   * Kalemin birincil rozeti KARAR'dır (G kolonu), ham muayene sonucu (E) değil.
   * Sebep: "Flag" bir karar değil, kaldırılmış bayraktır; karar ondan sonra verilir
   * (REWORK / REJECT / REPLACE / Accept). Eskiden E basılıyordu ve E ile G çeliştiğinde
   * (ör. E=Accept, G=REWORK) rozet kalemin bulunduğu grupla çelişiyordu.
   * Aksiyon tamamlanınca karar geçmiş zamana döner: REPLACE → REPLACED.
   * Ton kuralı (`kararTonu`) lib/ek-reject.ts'tedir: ek doğrulama satırının Result çipi de aynı kuralı kullanır (D-069).
   */
  const kararRozeti = (k: Kalem) => {
    if (k.durum === "degisti") return <Etiket tone="ok">REPLACED</Etiket>;
    if (k.karar) return <Etiket tone={kararTonu(k.karar)}>{k.karar}</Etiket>;
    return k.sonuc ? <Etiket>{k.sonuc}</Etiket> : null;
  };

  /** D-069: rework raporunun ek REJECT satırından açılmış kalemin mor çipi ("Rework report · Item 22"); öteki kalemlerde çizilmez. */
  const ekCipi = (x: Kalem, className?: string) => {
    const metin = ekKalemEtiketi(x);
    return metin ? <Etiket tone="hold" className={className}>{metin}</Etiket> : null;
  };

  const kalemGovde = (k: Kalem) => (
    <>
      <div className="flex flex-wrap items-baseline gap-x-2">
        <b className="text-sm">#{k.no} {k.kalem}</b>
        {ekCipi(k)}
        {k.snid && <span className="font-mono text-xs text-ink-3">{k.snid}</span>}
        {kararRozeti(k)}
        {sref(k)}
      </div>
      {/* OSI metinleri birebir gösterilir: özetlenmez, çevrilmez, kısaltılmaz. */}
      {k.bulgu && <div className="mt-1 border-l-2 border-line-strong pl-2 text-sm italic text-ink-2">{k.bulgu}</div>}
      {k.onerilenIs && <div className="mt-1 text-sm"><span className="text-ink-3">{cevir("Recommended action:")}</span> {k.onerilenIs}</div>}
      {k.kapsam && <div className="text-sm"><span className="text-ink-3">{cevir("OSI verification scope:")}</span> {k.kapsam}</div>}
      {k.kapanis && (
        <div className="mt-1 rounded-md border-l-2 border-ok bg-ok-bg/40 px-2 py-1 text-sm">
          <span className="text-ink-3">{cevir("Rework closure:")}</span>{" "}
          {[k.kapanis.item && `Item ${k.kapanis.item}`, k.kapanis.sonuc, k.kapanis.minwt && `min WT ${k.kapanis.minwt}`, k.kapanis.ekstra,
            k.kapanis.dispo && `disposition ${k.kapanis.dispo}${k.kapanis.dispoTarih ? ` (${k.kapanis.dispoTarih})` : ""}`].filter(Boolean).join(" · ")}
          {k.kapanis.dispoAd && <div className="text-xs text-ink-3">{cevir("dispo row:")} {k.kapanis.dispoAd}</div>}
          {/* Müfettişin cümlesi birebir; özetlenmez. */}
          {k.kapanis.not && <div className="mt-0.5 italic text-ink-2">{cevir("inspector: \"")}{k.kapanis.not}"</div>}
        </div>
      )}
      {k.eksik && !k.idariTip && (
        <Band tip="not" className="mt-1">{cevir("⚠ Not verified, no record in rework report.")}{k.eksikNot ? ` ${k.eksikNot}` : ""}</Band>
      )}
      {k.idariTip && (
        <div className="mt-1 rounded-md border-l-2 border-hold bg-hold-bg/40 px-2 py-1 text-sm">
          <span className="font-semibold text-hold">{k.idariTip === "osi-iade" ? cevir("RETURN TO OSI") : cevir("ADMINISTRATIVE CLOSURE")}</span>
          <span className="text-ink-3"> · {IDARI_AD[k.idariTip] ?? k.idariTip}</span>
          {k.idariTip === "osi-iade" && k.idariTarih && <span className="text-warn"> {cevir("· request")} {tarih(k.idariTarih)} ({gunFark(k.idariTarih)} {cevir("d)")}</span>}
          <div className="text-xs text-ink-2">{cevir("reason:")} {k.idariGerekce}{k.idariKanit ? cevir` · evidence: ${k.idariKanit}` : ""}</div>
          <div className="text-xs text-ink-3">{k.idariYapan} · {tarih(k.idariTarih)}</div>
        </div>
      )}
      {/* D-069: ek REJECT kaleminde bu metin satırın Evidence hücresidir; etiketi "Rework report (additional row):". */}
      {k.kaynakNot && <div className="mt-1 text-xs text-ink-2">{k.ekDogrulama ? cevir("Rework report (additional row):") : cevir("Carried over to Reject:")} {k.kaynakNot}</div>}
      {k.teyit && <div className="mt-1 text-xs text-ink-2">{k.teyit}</div>}
      {kimlikler(k)}
    </>
  );

  /**
   * D-040: Repair planning'den geri gönderilen bekleyen kalemin notu. Liste görünümü gerekçeyi ve işi ayrı
   * satırda gösterir; `kompakt` (Waiting Items penceresi) tek satırdır.
   */
  const geriNotu = (x: Kalem, kompakt: boolean) => {
    if (x.durum !== "bekliyor" || !x.geriTs) return null;
    if (kompakt) return (
      <div className="mt-2 rounded-md border-l-2 border-hold bg-hold-bg/40 px-2 py-1 text-xs">
        <span className="font-semibold text-hold">{cevir("Sent back from Repair planning")}</span>
        <span className="text-ink-3"> · {x.geriKim} · {tarih(x.geriTs)}</span>
        {/* D-043: gerekçe isteğe bağlı; yoksa ayraç da yazılmaz. */}
        {x.geriGerekce && <><span className="text-ink-3"> · </span><span className="text-ink-2">{x.geriGerekce}</span></>}
      </div>
    );
    return (
      <div className="mt-1 rounded-md border-l-2 border-hold bg-hold-bg/40 px-2 py-1 text-sm">
        <span className="font-semibold text-hold">{cevir("Sent back from Repair planning")}</span>
        <span className="text-ink-3"> · {x.geriKim} · {tarih(x.geriTs)}</span>
        {x.geriGerekce && <div className="text-xs text-ink-2">{cevir("reason:")} {x.geriGerekce}</div>}
        <div className="text-xs text-ink-3">
          {x.geriTamirId && (<>
            {x.geriIsIptal
              ? cevir`Job ${isKisa(x.geriTamirId)} is cancelled and stays readable (Sent back to Inspection verification).`
              : cevir`Job ${isKisa(x.geriTamirId)} continues with its other findings.`}{" "}
            <Link to="/komponent/$id" params={{ id: x.geriTamirId }} className="text-orange hover:underline">{cevir("open →")}</Link>{" "}
          </>)}
          {cevir`Previous route kept: ${x.rotaSecim ?? x.stOneri ?? "—"}, can be changed.`}
        </div>
      </div>
    );
  };

  /** D-039/D-040: "in Repair planning" rozeti kalemin işine bağlanır; iş birden çok bulgu taşıyorsa diğerleri yazılır. */
  const isRozeti = (k: Komponent, x: Kalem) => {
    const j = k.isler.find((i) => i.id === x.tamirId);
    if (!j) return <Etiket tone="info">{cevir("in Repair planning")}</Etiket>;
    const digerleri = j.bulgular.filter((b) => b.no !== x.no).map((b) => `#${b.no}`).join(", ");
    return (
      <Link to="/komponent/$id" params={{ id: j.id }} className="hover:opacity-80" title={cevir`Open job ${isKisa(j.id)} on its component page`}>
        <Etiket tone="info">{digerleri ? cevir`in Repair planning (with ${digerleri}) · job ${isKisa(j.id)} →` : cevir`in Repair planning · job ${isKisa(j.id)} →`}</Etiket>
      </Link>
    );
  };

  /**
   * D-047: ST seçimleri yalnız aktif ST'leri sunar; kalemin mevcut ST'si pasifse " · inactive" ekiyle ve seçilemez olarak görünür
   * (seçili değer olarak kalır). Sunucu da pasif ST'yi yeni seçimde reddeder.
   */
  const stSecenekleri = (mevcut: string | null | undefined) =>
    stg.filter((s) => s.aktif !== false || s.kod === mevcut).map((s) => (
      <option key={s.kod} value={s.kod} disabled={s.aktif === false}>{s.kod} — {s.ad}{s.aktif === false ? cevir(" · inactive") : ""}</option>
    ));

  /** D-050: stok paketi seçeneği; metin PN ile başlar, PKG no. sondadır (dört seçim listesinde aynı). */
  const paketSecenegi = (p: PaketSecenegi) => <option key={p.id} value={p.id}>{paketEtiketi(p)}</option>;
  /** D-076 (c): olası eşleşmenin çiftleri (sunucudan; eski yanıtta yok). */
  const pnCiftleri = veri.stokPnCiftleri ?? [];
  /** D-076: seçili paketin rozeti: olası eşleşmede mavi "possible match" ve gri gerekçe, değilse sarı "not a possible match" (uyarıdır, kaydı engellemez). */
  const olasiRozeti = (g: OlasiEslesmeGerekcesi | null) =>
    g ? <><Etiket tone="info">{cevir("possible match")}</Etiket><span>{olasiGerekceMetni(g)}</span></> : <Etiket tone="warn">{cevir("not a possible match")}</Etiket>;
  /** D-076: eşleşme yok cümlesi ("Possible matches" boşken, en az bir paket varken). */
  const eslesmeYokCumlesi = <p className="mt-1 text-xs text-ink-2">{cevir("No stock package with material left matches this Item's part number.")}</p>;
  /** D-076: "Show all stock packages ({0})" onay kutusu: düğme değildir (API ucu, yetki ve olay yok), yalnız listeyi genişletir; kaydedilmez. */
  const tumPaketKutusu = (n: number, isaretli: boolean, degistir: (v: boolean) => void) => (
    <label className="mt-1 flex items-center gap-2 text-xs text-ink-2">
      <input type="checkbox" checked={isaretli} onChange={(e) => degistir(e.target.checked)} />
      <span>{cevir("Show all stock packages ({0})", n)}</span>
    </label>
  );
  /** D-050: kapanmış kalemin paketini düzelten "Change stock package" bağlantısı "dogrula" yetkisinde görünür. */
  const paketDuzeltebilir = Boolean(ben && yetkiliMi(ben.rol, "dogrula"));
  /**
   * D-041/D-050: değişimde seçilen stok; tanım seçim anındaki kopyadır. Pakete bağlı kalemde PKG no. (bitmişse "(package finished)"),
   * paketsiz eski seçimde "package not recorded". Durumu "degisti" (changed) olan salt değişim kaleminde "Change stock package" kartın
   * içinde liste açar: paketsiz eski seçimde başta seçilemeyen yer tutucu (Save başka değer seçilene kadar kapalı), "— not from stock —",
   * D-076: "Possible matches", "Other stock packages" ve "Finished packages (material already taken)"; varsayılan liste olası eşleşenleri,
   * kalemin bugünkü paketini ve seçili paketi taşır, gerisini "Show all stock packages" açar (`paketDegisimListesi`). Seçim satırı yalnız
   * rozeti ve gerekçeyi taşır; yer tutucu ya da "— not from stock —" seçiliyken çizilmez. Save, Cancel ve sunucu ucu değişmez.
   */
  const stokSatiri = (k: Komponent, x: Kalem) => {
    const duzeltilir = paketDuzeltebilir && x.sinif === "degisim" && x.durum === "degisti";
    if (!x.stokPn && !duzeltilir) return null;
    const acik = paketDegisim?.id === x.id ? paketDegisim : null;
    const acikDeger = acik ? acik.deger : null;
    const baslangic = x.stokPaket ? x.stokPaket.id : x.stokPn ? "mevcut" : "";
    const liste = acik
      ? paketDegisimListesi(x, veri.paketler, veri.bitenPaketler, pnCiftleri, { mevcutId: x.stokPaket?.id ?? null, secimId: acik.deger, hepsi: acik.hepsi })
      : null;
    return (
      <>
        <div className="mt-1 text-xs text-ink-2">
          {x.stokPn && (<>
            {cevir`from stock ${x.stokPn}`}{x.stokTanim ? ` · ${x.stokTanim}` : ""}
            {x.stokPaket
              ? ` · ${x.stokPaket.pkg}${x.stokPaket.bitti ? ` ${cevir("(package finished)")}` : ""}`
              : <> · <span className="text-warn">{cevir("package not recorded")}</span></>}
          </>)}
          {duzeltilir && (
            <button type="button" className={`${x.stokPn ? "ml-2 " : ""}font-semibold text-orange hover:opacity-80`} onClick={() => setPaketDegisim({ id: x.id, deger: baslangic, hepsi: false })}>
              {cevir("Change stock package")}
            </button>
          )}
        </div>
        {acik && acikDeger !== null && liste !== null && (
          <div className="mt-2">
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <span className="text-ink-3">{cevir("From stock")}</span>
              <select className={girdi + " max-w-lg"} value={acikDeger} onChange={(e) => setPaketDegisim({ ...acik, deger: e.target.value })}>
                {!x.stokPaket && x.stokPn && <option value="mevcut" disabled>{cevir("— package not recorded (current: {0}) —", x.stokPn)}</option>}
                <option value="">{cevir("— not from stock —")}</option>
                {liste.olasi.length > 0 && <optgroup label={cevir("Possible matches")}>{liste.olasi.map(paketSecenegi)}</optgroup>}
                {liste.diger.length > 0 && <optgroup label={cevir("Other stock packages")}>{liste.diger.map(paketSecenegi)}</optgroup>}
                {liste.biten.length > 0 && <optgroup label={cevir("Finished packages (material already taken)")}>{liste.biten.map(paketSecenegi)}</optgroup>}
              </select>
            </div>
            {liste.secili && <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-ink-3">{olasiRozeti(liste.gerekce)}</div>}
            {liste.eslesmeYok && eslesmeYokCumlesi}
            {liste.kutu && tumPaketKutusu(liste.toplam, acik.hepsi, (v) => setPaketDegisim({ ...acik, hepsi: v }))}
            <div className="mt-2 flex gap-2">
              <Buton kucuk tur="birincil" islem="dogrula" disabled={acikDeger === "mevcut"}
                onClick={async () => { if (await dene(() => api.post(`/muayene/${rn}/stok-paket`, { kod: k.kod, no: x.no, paketId: acikDeger || null }))) setPaketDegisim(null); }}>
                {cevir("Save")}
              </Buton>
              <Buton kucuk onClick={() => setPaketDegisim(null)}>{cevir("Cancel")}</Buton>
            </div>
          </div>
        )}
      </>
    );
  };

  /**
   * "Waiting Items" penceresi, D-039 1. adım. Bileşen değil düz fonksiyon: her çizimde yeniden
   * bağlanıp yeni ürün ID kutusunun odağını kaybetmesin. Kalemler taze veriden okunur (grubun rota
   * seçimi `/rota` + yenile yapar). Öneri sunucudan gelir (@oasis/domain) ve D-042'den beri kendiliğinden
   * uygulanmaz: bulgunun altında sarı soru çıkar, kişi "Send as one job" / "Attach to running job" ya da
   * "Keep separate" der. Grup tek iş açar, bağlama yeni iş açmaz, "Separate" grubu ya da bağlamayı geri alır.
   * Öneri eşine göre kabul edilir: bulgu önerinin eşiyle aynı işe gitmiyorsa (cevaplanmamış, "Keep separate" ya da elle
   * başka bir bulguya/işe katılmış) sunucu "not accepted" loglar; özet satırı ve grup/bağlama satırındaki not bunu söyler.
   * Salt değişim kalemleri (ST-07, ST-08, ST-11) iş açmaz ve gruplanmaz; D-041 ile stoktan seçilebilir.
   */
  const bekleyenPenceresi = (acilan: Komponent) => {
    const dk = veri.komponentler.find((k) => k.kod === acilan.kod) ?? acilan;
    const stOf = (x: Kalem) => stg.find((s) => s.kod === (x.rotaSecim ?? x.stOneri));
    // Sunucunun degisim ucuyla aynı hesap: rotasız kalem salt değişim sayılır.
    const repairMi = (x: Kalem) => ((stOf(x)?.tip ?? x.rotaTipi) ?? "degisim") !== "degisim";
    const bekleyenler = dk.kalemler.filter((x) => x.sinif === "degisim" && x.durum === "bekliyor").sort((a, b) => a.no - b.no);
    const noyaGore = new Map(bekleyenler.map((x) => [x.no, x]));
    const acikIsler = dk.isler.filter((j) => !j.bitti);
    const bOf = (x: Kalem): Birlesim => birlesim[x.no] ?? { tur: "ayri" };
    /** Grubun geçerli öncüsü: bekleyen tamir kalemi olmalı ve kendi kararı da kendisini göstermeli. */
    const liderOf = (x: Kalem): number | null => {
      const b = bOf(x);
      if (b.tur !== "grup") return null;
      const l = noyaGore.get(b.lider);
      if (!l || !repairMi(l)) return null;
      const bl = bOf(l);
      return bl.tur === "grup" && bl.lider === l.no ? l.no : null;
    };
    const gruplar = new Map<number, Kalem[]>();
    for (const x of bekleyenler) {
      if (!repairMi(x)) continue;
      const l = liderOf(x);
      if (l !== null) gruplar.set(l, [...(gruplar.get(l) ?? []), x]);
    }
    /** İki ya da daha çok bulgulu grubun öncüsü; tek kalan "grup" ayrı iş gibi çizilir. */
    const uyeLideri = (x: Kalem): number | null => {
      const l = repairMi(x) ? liderOf(x) : null;
      return l !== null && (gruplar.get(l)?.length ?? 0) >= 2 ? l : null;
    };
    const isOf = (x: Kalem): IsOzeti | null => {
      if (!repairMi(x) || uyeLideri(x) !== null) return null;
      const b = bOf(x);
      return b.tur === "is" ? acikIsler.find((j) => j.id === b.tamirId) ?? null : null;
    };
    /**
     * D-035: bulgu kesim rotasında mı (ST-03, ST-04, ST-05, ST-06)? Grup üyesi öncünün ST'sine bakar (grubun rotası öncüden),
     * tek bulgu ve süren işe bağlanan bulgu kendi ST'sine. Sunucunun degisim ucuyla aynı hesap.
     */
    const kesimliMi = (x: Kalem): boolean => {
      if (!repairMi(x)) return false;
      const l = uyeLideri(x);
      return kesimRotasiMi(stOf((l !== null ? noyaGore.get(l) : undefined) ?? x));
    };
    /** D-035: kesimsiz kalemi saha inspektörü ("dogrula"), kesim rotalı kalemi kalite lideri ("kesimPlani") gönderir; PM ve sistem yöneticisi her ikisini. */
    const dogrulaYetkili = Boolean(ben && yetkiliMi(ben.rol, "dogrula"));
    const kesimYetkili = Boolean(ben && yetkiliMi(ben.rol, "kesimPlani"));
    /**
     * D-042: bulgunun cevap bekleyebilecek önerisi. Grup önerisi yalnız üyede (öncünün kendi kaydı soru değildir)
     * ve öncü hâlâ bekleyen tamir kalemiyken; bağlama önerisi iş hâlâ çalışıyorken geçerlidir.
     */
    const gecerliOneri = (x: Kalem): Extract<AyniTamirOnerisi, { tur: "grup" | "is" }> | null => {
      const o = x.oneri;
      if (!o || !repairMi(x)) return null;
      if (o.tur === "grup") {
        const l = noyaGore.get(o.lider);
        return o.lider !== x.no && l && repairMi(l) ? o : null;
      }
      if (o.tur === "is") return acikIsler.some((j) => j.id === o.tamirId) ? o : null;
      return null;
    };
    /** Bulgunun bu pencerede gideceği iş (sunucunun degisim hesabıyla aynı): grubu, bağlandığı süren iş ya da kendi yeni işi. */
    const isAnahtari = (x: Kalem): string => {
      const l = uyeLideri(x);
      if (l !== null) return `g:${l}`;
      const j = isOf(x);
      return j ? `j:${j.id}` : `g:${x.no}`;
    };
    /**
     * Önerinin durumu, EŞİNE göre: grup önerisi bulgu öncüsüyle (o.lider) aynı işe gidiyorsa, bağlama önerisi bulgu
     * önerilen işe bağlıysa kabul edilmiştir. Elle başka bir bulguyla gruplanmak ya da başka işe bağlanmak kabul sayılmaz
     * (sunucu o durumda "not accepted" yazar); o zaman "Keep separate" dendiyse "red", denmediyse "acik".
     */
    const oneriDurumu = (x: Kalem): "yok" | "acik" | "kabul" | "red" => {
      const o = gecerliOneri(x);
      if (!o) return "yok";
      const l = o.tur === "grup" ? noyaGore.get(o.lider) : undefined;
      const kabul = o.tur === "grup" ? l !== undefined && isAnahtari(l) === isAnahtari(x) : isOf(x)?.id === o.tamirId;
      if (kabul) return "kabul";
      return oneriCevap[x.no] === "red" ? "red" : "acik";
    };
    const cevapla = (noListesi: number[], cevap: OneriCevabi | null) => setOneriCevap((p) => {
      const y = { ...p };
      for (const n of noListesi) { if (cevap) y[n] = cevap; else delete y[n]; }
      return y;
    });
    /** Öneri çiftinin adı: "#8 + #9" (bağlamada #lider süren işteki bulgudur). */
    const ciftAdi = (x: Kalem, o: { lider: number }) => `#${o.lider} + #${x.no}`;
    /** Grup ya da bağlama satırına girmiş ama önerisi kabul edilmemiş bulgunun kısa notu (özet satırıyla aynı söz). */
    const oneriNotu = (x: Kalem, ek = "") => {
      const o = gecerliOneri(x);
      const d = oneriDurumu(x);
      if (!o || (d !== "acik" && d !== "red")) return null;
      return <div className={`mt-0.5 text-xs ${d === "acik" ? "text-warn" : "text-ink-3"} ${ek}`}>{d === "acik" ? cevir`proposal ${ciftAdi(x, o)} open` : cevir`proposal ${ciftAdi(x, o)} not accepted`}</div>;
    };
    const tik = (x: Kalem) => secim[x.no]?.isaretli ?? false;
    const tikle = (noListesi: number[], deger: boolean) => setSecim((p) => {
      const y = { ...p };
      for (const n of noListesi) y[n] = { isaretli: deger, yeniId: p[n]?.yeniId ?? "", stokPaketId: p[n]?.stokPaketId ?? "" };
      return y;
    });
    const ata = (degisen: Record<number, Birlesim>) => setBirlesim((p) => ({ ...p, ...degisen }));
    const nolar = (l: Kalem[]) => l.map((m) => `#${m.no}`).join(" + ");
    const meta = (x: Kalem) => {
      const st = stOf(x);
      return `${st ? `${st.kod} · ${repairMi(x) ? cevir("repair") : cevir("Replacement only")}` : cevir("route card not selected")}${x.siparisPn ? ` · PN ${x.siparisPn}` : ""}`;
    };
    const kutu = (x: Kalem, baslik?: ReactNode, uyeler?: Kalem[]) => {
      const hepsi = uyeler ?? [x];
      // D-035: "dogrula" yetkisi olmayan (kalite lideri) yalnız kesim rotalı kalemi işaretler; kesimsiz kalemi saha inspektörü gönderir.
      const kilitli = !dogrulaYetkili && !kesimliMi(x) && !hepsi.some(tik);
      return (
        <label className="flex min-w-0 flex-1 items-start gap-2 text-sm" title={kilitli ? cevir("Items without cuts are sent by the Field Inspector.") : undefined}>
          <input type="checkbox" className="mt-1" checked={hepsi.every(tik)} disabled={kilitli} onChange={(e) => tikle(hepsi.map((m) => m.no), e.target.checked)} />
          <span className="min-w-0 flex-1">
            <b>{baslik ?? <>#{x.no} {x.kalem}</>}</b>
            {/* D-069: kalemin kendi satırında başlıktan sonra mor çip; grup satırında çip üye satırlarındadır. */}
            {!baslik && ekCipi(x, "ml-2")}
            <span className="ml-2 text-xs text-ink-3">{meta(x)}</span>
          </span>
        </label>
      );
    };

    /** "+ Same repair as…": bulguyu elle bu komponentin başka bir bekleyen kalemine ya da süren işine bağlar. */
    const katil = (x: Kalem, deger: string) => {
      if (deger.startsWith("j:")) { ata({ [x.no]: { tur: "is", tamirId: deger.slice(2) } }); return; }
      const y = deger.startsWith("k:") ? noyaGore.get(Number(deger.slice(2))) : undefined;
      if (!y) return;
      const yIs = isOf(y);
      if (yIs) { ata({ [x.no]: { tur: "is", tamirId: yIs.id } }); return; }
      const yLider = uyeLideri(y);
      const lider = yLider ?? y.no;
      const uyeler = [...(yLider !== null ? gruplar.get(yLider) ?? [] : [y]), x];
      ata({ [x.no]: { tur: "grup", lider }, [lider]: { tur: "grup", lider } });
      // Grup tek kutuyla işaretlenir: birleşince hepsi aynı işarette olsun (yarım işaretli grup gönderilemez).
      tikle(uyeler.map((m) => m.no), uyeler.some(tik));
    };
    const ayniSecici = (x: Kalem) => {
      const secenekler: { deger: string; etiket: string }[] = [];
      const gorulen = new Set<number>();
      for (const y of bekleyenler) {
        if (y.no === x.no || !repairMi(y)) continue;
        const l = uyeLideri(y);
        if (l === null) { secenekler.push({ deger: `k:${y.no}`, etiket: `#${y.no} ${y.kalem}` }); continue; }
        if (gorulen.has(l)) continue;
        gorulen.add(l);
        const ls = noyaGore.get(l);
        secenekler.push({ deger: `k:${l}`, etiket: cevir`${nolar(gruplar.get(l) ?? [])} · ${(ls && stOf(ls)?.kod) ?? "—"} (group)` });
      }
      return (
        <select className="h-kontrol-kucuk w-64 rounded-md border border-line-strong bg-bg px-2 text-sm text-ink outline-none transition focus:border-orange disabled:opacity-60"
          value="" onChange={(e) => katil(x, e.target.value)}>
          <option value="">{cevir("+ Same repair as…")}</option>
          <optgroup label={cevir`Waiting items of ${dk.ad}`}>
            {secenekler.length ? secenekler.map((s) => <option key={s.deger} value={s.deger}>{s.etiket}</option>) : <option value="-" disabled>{cevir("none")}</option>}
          </optgroup>
          <optgroup label={cevir`Running jobs of ${dk.ad}`}>
            {acikIsler.length
              ? acikIsler.map((j) => <option key={j.id} value={`j:${j.id}`}>{`${isKisa(j.id)} · #${j.liderNo ?? j.bulgular[0]?.no ?? ""} ${j.bulgular[0]?.kalem ?? ""}`}</option>)
              : <option value="-" disabled>{cevir("none")}</option>}
          </optgroup>
        </select>
      );
    };

    /** Grup satırı: tek kutu, kanıt, üyeler, öncünün rotası ("Route of the job"). */
    const grupSatiri = (oncu: Kalem, uyeler: Kalem[]) => {
      const st = stOf(oncu);
      const kanitli = uyeler.filter((m) => m.no !== oncu.no).map((m) => ({ m, o: m.oneri })).find(({ o }) => o?.tur === "grup" && o.lider === oncu.no);
      const kanit = (kanitli?.o && ayniTamirKanitMetni(kanitli.o.kanit, kanitli.m.no)) || cevir("Joined by hand.");
      const mrbIlk = uyeler.map((m) => (m.mrbKimlikler ?? "").split("||")[0]?.trim() ?? "");
      const ortakMrb = mrbIlk[0] && mrbIlk.every((s) => s === mrbIlk[0]) ? mrbIlk[0] : null;
      const farkli = uyeler.filter((m) => m.no !== oncu.no && stOf(m)?.kod !== st?.kod);
      const oncuOnde = [oncu, ...uyeler.filter((m) => m.no !== oncu.no)];
      return (
        <div key={`g${oncu.no}`} className="rounded-md border border-orange/40 p-3">
          <div className="flex flex-wrap items-start justify-between gap-2">
            {kutu(oncu, cevir`Same repair → one job: ${nolar(uyeler)}`, uyeler)}
            {/* D-042: "Separate" "Send as one job"u geri alır; satırlar ayrılır, öneri yeniden açık soru olur. */}
            <Buton kucuk onClick={() => { ata(Object.fromEntries(uyeler.map((m) => [m.no, { tur: "ayri" } as Birlesim]))); cevapla(uyeler.map((m) => m.no), null); }}>{cevir("Separate")}</Buton>
          </div>
          <div className="mt-2 text-xs text-ink-2">{kanit}</div>
          {ortakMrb && <div className="mt-0.5 text-xs text-ink-3">{cevir`These findings share the MRB identity ${ortakMrb}; shown for reference, not used to match.`}</div>}
          <div className="mt-2 space-y-2 border-l-2 border-line-strong pl-3">
            {oncuOnde.map((m) => (
              <div key={m.id}>
                <div className="flex flex-wrap items-baseline gap-x-2">
                  <b className="text-sm">#{m.no} {m.kalem}</b>
                  {ekCipi(m)}
                  {m.no === oncu.no && <Etiket tone="info">{cevir("lead finding")}</Etiket>}
                  <span className="text-xs text-ink-3">{[stOf(m)?.kod, m.siparisPn && `PN ${m.siparisPn}`].filter(Boolean).join(" · ")}</span>
                </div>
                {/* OSI metni birebir; özetlenmez. D-069: ek REJECT kaleminde önerilen iş ve bulgu boştur, alıntı satırın Evidence metnidir. */}
                {ekAlintisi(m) && (
                  <div className="mt-0.5 text-sm text-ink-2"><i>“{ekAlintisi(m)}”</i>{refler(m.rworkRefleri ?? [], riser.reworkDokumanId, riser.reworkRapor, true, riser.reworkSayfaAraligi)}</div>
                )}
                {geriNotu(m, true)}
                {oneriNotu(m)}
              </div>
            ))}
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-2 text-sm">
            <span className="text-ink-3">{cevir("Route of the job")}</span>
            <select className={girdi + " max-w-[320px]"} value={oncu.rotaSecim ?? oncu.stOneri ?? ""}
              onChange={(e) => void dene(() => api.post(`/muayene/${rn}/rota`, { kod: dk.kod, no: oncu.no, st: e.target.value || null }))}>
              <option value="">{cevir("not selected")}</option>
              {stSecenekleri(oncu.rotaSecim ?? oncu.stOneri)}
            </select>
            {st && <Etiket>{st.rotaKod}</Etiket>}
            {st?.malzeme && <Etiket tone="warn">{cevir("requires material")}</Etiket>}
            <Etiket>{cevir("repair")}</Etiket>
            <span className="text-xs text-ink-3">
              {cevir`from lead finding #${oncu.no}`}
              {farkli.map((m) => <span key={m.no}> · {cevir`${`#${m.no}`} has route ${stOf(m)?.kod ?? "—"}`}</span>)}
            </span>
          </div>
          <div className="mt-2 text-xs text-ink-2">{cevir`One job for these findings: one ${st?.rotaKod ?? "—"} route, one set of welds and fit-up forms, instead of one per finding. When the job is finished, ${nolar(uyeler)} all count as done here.`}</div>
        </div>
      );
    };

    /**
     * Süren işe bağlanmış bulgu (D-042: yalnız "Attach to running job" ya da "+ Same repair as…" ile bağlanınca çizilir).
     * "Separate" bağlamayı geri alır; bulgu kendi satırına ve açık öneriye döner.
     */
    const bagSatiri = (x: Kalem, j: IsOzeti) => {
      const o = x.oneri;
      const oi = o && o.tur === "is" && o.tamirId === j.id ? o : null;
      const lider = oi ? oi.lider : j.liderNo ?? j.bulgular[0]?.no ?? 0;
      const kayitYok = j.kayitYokAdimlar.length ? " · " + cevir`${j.kayitYokAdimlar.join(", ")} closed with No record available` : "";
      return (
        <div key={x.id} className="rounded-md border border-orange/40 p-3">
          {kutu(x)}
          {geriNotu(x, true)}
          {oneriNotu(x, "pl-5")}
          <div className="mt-2 rounded-md border border-line bg-surface-2 p-2">
            <div className="text-sm"><b>{cevir`Same repair as #${lider} — running job ${isKisa(j.id)} (${adimMetni(j)})`}</b></div>
            <div className="mt-0.5 text-xs text-ink-2">{(oi && ayniTamirKanitMetni(oi.kanit, x.no)) || cevir("Joined by hand.")}</div>
            <div className="mt-0.5 text-xs text-ink-3">
              {cevir`Job ${isKisa(j.id)} · ${j.riserRn} ${j.hat} · ${j.poz ?? ""} ${j.is} · ${j.rotaKod} · covers ${j.bulgular.map((b) => `#${b.no} ${b.kalem}`).join(", ")}`}{kayitYok}
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              {/* D-042: bağlama zaten seçilmiş (bu satır ancak o zaman çizilir); tek eylem geri almaktır. */}
              <Buton kucuk onClick={() => { ata({ [x.no]: { tur: "ayri" } }); cevapla([x.no], null); }}>{cevir("Separate")}</Buton>
            </div>
            <div className="mt-1 text-xs text-ink-2">{cevir`No new job and no new route. #${x.no} then shows “in Repair planning (with #${lider})” and counts as done when job ${isKisa(j.id)} is finished. Separate undoes the attach.`}</div>
          </div>
        </div>
      );
    };

    /**
     * D-042 sarı soru: "Same repair as #8?" (ya da "… — running job …?"), dayanak ve iki düğme. Cevap verilene
     * kadar hiçbir şey birleşmez; "+ Same repair as…" seçicisi bu bulguda gizlidir (cevap iki düğmedir).
     */
    const oneriBlogu = (x: Kalem, o: Extract<AyniTamirOnerisi, { tur: "grup" | "is" }>) => {
      const j = o.tur === "is" ? acikIsler.find((i) => i.id === o.tamirId) ?? null : null;
      const lider = o.tur === "grup" ? noyaGore.get(o.lider) : undefined;
      const st = (lider && stOf(lider)?.kod) ?? "—";
      const kanit = ayniTamirKanitMetni(o.kanit, x.no);
      return (
        <div className="mt-2 rounded-md border border-warn/20 bg-warn-bg px-3 py-2">
          <div className="text-sm font-semibold text-warn">
            {j ? cevir`Same repair as #${o.lider} — running job ${isKisa(j.id)} (${adimMetni(j)})?` : cevir`Same repair as #${o.lider}?`}
          </div>
          {kanit && <div className="mt-0.5 text-xs text-ink-2">{kanit}</div>}
          {j && (
            <div className="mt-0.5 text-xs text-ink-3">
              {cevir`Job ${isKisa(j.id)} · ${j.riserRn} ${j.hat} · ${j.poz ?? ""} ${j.is} · ${j.rotaKod} · covers ${j.bulgular.map((b) => `#${b.no} ${b.kalem}`).join(", ")}`}
            </div>
          )}
          <div className="mt-2 flex flex-wrap items-center gap-2">
            {j
              ? <Buton kucuk onClick={() => { ata({ [x.no]: { tur: "is", tamirId: j.id } }); cevapla([x.no], "kabul"); }}>{cevir("Attach to running job")}</Buton>
              : <Buton kucuk onClick={() => { katil(x, `k:${o.lider}`); cevapla([x.no], "kabul"); }}>{cevir("Send as one job")}</Buton>}
            <Buton kucuk onClick={() => cevapla([x.no], "red")}>{cevir("Keep separate")}</Buton>
          </div>
          <div className="mt-1 text-xs text-ink-2">
            {j
              ? cevir`Nothing is merged until you choose. Attach to running job: #${x.no} joins job ${isKisa(j.id)}, with no new job and no new route. Keep separate: #${x.no} goes as its own job.`
              : cevir`Nothing is merged until you choose. Send as one job: ${ciftAdi(x, o)} go down as one ${st} job. Keep separate: each goes as its own job.`}
          </div>
        </div>
      );
    };

    /**
     * Tek tamir kalemi: kendi işi olarak iner; "+ Same repair as…" ile elle birleştirilebilir. D-042: önerisi açıksa
     * altında sarı soru durur (seçici gizli); "Keep separate" dendiyse gri not ve seçici gelir. Grubun öncüsü
     * açık soruyu kısa notla gösterir.
     */
    const tekSatir = (x: Kalem) => {
      const o = x.oneri;
      const olasiMetin = o?.tur === "olasi" ? o.kanit.reworkMetni ?? o.kanit.siparisNotu : null;
      const go = gecerliOneri(x);
      const durum = oneriDurumu(x);
      // Öncü: bu bulguya grup önerisi açık bekleyen üyeler ("same repair proposed with #9 (see below)").
      const acikUyeler = bekleyenler.filter((m) => m.no !== x.no && m.oneri?.tur === "grup" && m.oneri.lider === x.no && oneriDurumu(m) === "acik");
      return (
        <div key={x.id} className="rounded-md border border-line p-2">
          {kutu(x)}
          {geriNotu(x, true)}
          {go && durum === "acik"
            ? oneriBlogu(x, go)
            : (
              <div className="mt-2 flex flex-wrap items-center gap-2 pl-5">
                {ayniSecici(x)}
                {go && durum === "red" && (
                  <span className="text-xs text-ink-3">
                    {go.tur === "is"
                      ? cevir`Same-repair proposal not accepted (${ciftAdi(x, go)}, running job ${isKisa(go.tamirId)}); #${x.no} goes as its own job.`
                      : cevir`Same-repair proposal not accepted (${ciftAdi(x, go)}); each goes as its own job.`}
                  </span>
                )}
                {acikUyeler.length > 0 && <span className="text-xs text-ink-3">{cevir`same repair proposed with ${acikUyeler.map((m) => `#${m.no}`).join(", ")} (see below)`}</span>}
                {o?.tur === "olasi" && <span className="text-xs text-ink-2">{cevir`Possibly same repair as #${o.no}`}{olasiMetin && <> · <i>“{olasiMetin}”</i></>}</span>}
                {!o && <span className="text-xs text-ink-3">{cevir("no same repair proposed for this item")}</span>}
              </div>
            )}
        </div>
      );
    };

    /** Salt değişim kalemi: işaret + yeni ürün ID; gruplanmaz. D-050: işaretliyken "From stock" aktif stok paketlerinden seçilir. */
    const stokSec = (no: number, paketId: string) =>
      setSecim((p) => ({ ...p, [no]: { isaretli: p[no]?.isaretli ?? true, yeniId: p[no]?.yeniId ?? "", stokPaketId: paketId } }));
    /**
     * D-050: liste en az bir paket kaydı varken çizilir; bütün paketler bitmişse yalnız "— not from stock —" taşır. Hiç paket kaydı
     * yokken yerinde "No stock packages recorded…" durur (liste olmadığı için "Finished packages are not listed." de yok).
     */
    const paketKaydiVar = veri.paketler.length + veri.bitenPaketler.length > 0;
    const degisimSatiri = (x: Kalem) => {
      const stokPaketId = secim[x.no]?.stokPaketId ?? "";
      // D-076: varsayılan listede yalnız olası eşleşenler (@oasis/domain `stokSecenekleri`); öteki aktif paketler "Show all stock packages"
      // ile gelir. Sistem kendiliğinden seçmez, varsayılan "— not from stock —". Seçili paket bütün listeden aranır ve her zaman listede
      // kalır. Liste yalnız işaretli satırda gösterildiği için eşleşme de yalnız o zaman hesaplanır. Sıra kararlı: aynı PN'in paketleri sunucu sırasında.
      const hepsi = tumPaketler[x.no] ?? false;
      const liste = tik(x) ? stoktanListesi(x, veri.paketler, pnCiftleri, stokPaketId, hepsi) : null;
      const secili = liste?.secili ?? null;
      return (
        <div key={x.id} className="rounded-md border border-line p-2">
          {kutu(x)}
          {geriNotu(x, true)}
          {tik(x) && (<>
            <input className={girdi + " mt-2"} placeholder={cevir("new product ID (if known)")} value={secim[x.no]?.yeniId ?? ""}
              onChange={(e) => { const v = e.target.value; setSecim((p) => ({ ...p, [x.no]: { isaretli: true, yeniId: v, stokPaketId: p[x.no]?.stokPaketId ?? "" } })); }} />
            {paketKaydiVar && liste ? (
              <div className="mt-2">
                <div className="flex flex-wrap items-center gap-2 text-sm">
                  <span className="text-ink-3">{cevir("From stock")}</span>
                  <select className={girdi + " max-w-lg"} value={secili ? stokPaketId : ""} onChange={(e) => stokSec(x.no, e.target.value)}>
                    <option value="">{cevir("— not from stock —")}</option>
                    {liste.olasi.length > 0 && <optgroup label={cevir("Possible matches")}>{liste.olasi.map(paketSecenegi)}</optgroup>}
                    {liste.diger.length > 0 && <optgroup label={cevir("Other stock packages")}>{liste.diger.map(paketSecenegi)}</optgroup>}
                  </select>
                </div>
                {secili && (
                  <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-ink-3">
                    {olasiRozeti(liste.gerekce)}
                    <span>{cevir("Package {0}: {1} {2} · the selection does not deduct it", secili.pkg, secili.miktar, secili.birim)}</span>
                  </div>
                )}
                {liste.eslesmeYok && eslesmeYokCumlesi}
                {liste.kutu && tumPaketKutusu(liste.toplam, hepsi, (v) => setTumPaketler((p) => ({ ...p, [x.no]: v })))}
                {/* D-050 D-046: liste çizildiği her durumda (seçim olsa da olmasa da). */}
                <p className="mt-1 text-xs text-ink-3">{cevir("Finished packages are not listed.")}</p>
              </div>
            ) : (
              <p className="mt-2 text-xs text-ink-3">{cevir("No stock packages recorded. Packages are added in Registers → Stock.")}</p>
            )}
          </>)}
        </div>
      );
    };

    const satirlar: ReactNode[] = [];
    for (const x of bekleyenler) {
      const l = uyeLideri(x);
      if (l !== null) { if (l === x.no) satirlar.push(grupSatiri(x, gruplar.get(l) ?? [x])); continue; }
      if (!repairMi(x)) { satirlar.push(degisimSatiri(x)); continue; }
      const j = isOf(x);
      satirlar.push(j ? bagSatiri(x, j) : tekSatir(x));
    }

    // Özet: n işaretli tamir bulgusu → m yeni iş (grup tek iş, bağlama sıfır iş); D-042 açık / reddedilen öneriler.
    const repairVar = bekleyenler.some(repairMi);
    const tikliRepair = bekleyenler.filter((x) => repairMi(x) && tik(x));
    const tikliGruplar = [...gruplar.entries()].filter(([, u]) => u.length >= 2 && u.some(tik));
    const tikliBaglar = tikliRepair.filter((x) => isOf(x) !== null);
    const yeniIs = tikliRepair.filter((x) => uyeLideri(x) === null && isOf(x) === null).length
      + new Set(tikliRepair.map(uyeLideri).filter((l) => l !== null)).size;
    const tiksizler = bekleyenler.filter((x) => !tik(x));
    // D-042: gönderilecek bulgunun cevaplanmamış ya da reddedilmiş önerisi özet satırında yazar ("proposal #8 + #9 open").
    const oneriler = tikliRepair.flatMap((x) => {
      const o = gecerliOneri(x);
      const d = oneriDurumu(x);
      return o && (d === "acik" || d === "red") ? [{ x, o, acik: d === "acik" }] : [];
    });
    // Düğmelerin sonucunu anlatan cümleler yalnız bulgu kendi satırındayken doğrudur (grupta ya da başka işe bağlıyken değil).
    const kendiSatiri = (x: Kalem) => uyeLideri(x) === null && isOf(x) === null;
    /** Bulgunun açacağı yeni işin ST'si: grubun öncüsünün ya da kendisinin (süren işe bağlıysa yok). */
    const yeniIsSt = (x: Kalem) => {
      if (isOf(x)) return undefined;
      const l = uyeLideri(x);
      const y = l !== null ? noyaGore.get(l) : x;
      return y ? stOf(y)?.kod : undefined;
    };
    const oneriCumleleri = oneriler.flatMap(({ x, o, acik }) => {
      if (o.tur === "is") {
        if (!acik) return [];
        return [kendiSatiri(x)
          ? cevir`The proposal under #${x.no} is not answered yet: Attach to running job adds it to job ${isKisa(o.tamirId)}, Keep separate sends it as its own job`
          : cevir`The proposal under #${x.no} is not answered yet`];
      }
      const l = noyaGore.get(o.lider);
      const ikisi = Boolean(l && tik(l));
      const st = l && yeniIsSt(l);
      return [
        ...(ikisi && st && st === yeniIsSt(x) ? [cevir`${`#${o.lider}`} and ${`#${x.no}`} go down as separate jobs on ${st}`] : []),
        ...(acik ? [ikisi && l && kendiSatiri(x) && kendiSatiri(l)
          ? cevir`The proposal under #${x.no} is not answered yet: Send as one job makes it 1 job, Keep separate keeps 2 jobs`
          : cevir`The proposal under #${x.no} is not answered yet`] : []),
      ];
    });
    const ozetSatiri = cumleler([
      ...tikliGruplar.map(([l, u]) => { const ls = noyaGore.get(l); return cevir`${nolar(u)} go down as one job on ${(ls && stOf(ls)?.kod) ?? "—"}`; }),
      ...tikliBaglar.map((x) => { const j = isOf(x)!; return cevir`${`#${x.no}`} joins running job ${isKisa(j.id)} (with ${j.bulgular.map((b) => `#${b.no}`).join(", ")})`; }),
      ...oneriCumleleri,
      ...(tiksizler.length ? [cevir`Not ticked, stay waiting: ${tiksizler.map((x) => { const st = stOf(x); return `#${x.no}${st ? ` (${st.kod})` : ""}`; }).join(", ")}.`] : []),
    ]);
    const tikliler = bekleyenler.filter(tik);
    // Tek işaretli kalem süren işe bağlanıyorsa düğme ne yapılacağını söyler ("Attach #9 to job 05d8b642").
    const ilk = tikliler.length === 1 ? tikliler[0] : undefined;
    const ilkIs = ilk ? isOf(ilk) : null;
    const tekBag = ilk && ilkIs ? { x: ilk, j: ilkIs } : null;

    /**
     * D-035: kesim planı açılacak işler: işaretli kesim rotalı tek bulgular (süren işe bağlananlar hariç; o işin planı
     * geçerlidir) ve öncüsü kesim rotalı işaretli gruplar. Anahtar "g:<öncü no>", bulgular öncü önde.
     */
    const planIsleri: { anahtar: string; nolar: number[] }[] = [];
    for (const x of tikliler) {
      if (!kesimliMi(x) || isOf(x) !== null) continue;
      const l = uyeLideri(x) ?? x.no;
      if (l !== x.no) continue;
      const uyeler = (uyeLideri(x) !== null ? gruplar.get(l) ?? [x] : [x]).map((m) => m.no).filter((n) => n !== l).sort((p, q) => p - q);
      planIsleri.push({ anahtar: `g:${l}`, nolar: [l, ...uyeler] });
    }
    /** Süren işe bağlanan kesim rotalı bulgu da kalite liderinin işidir (plan taşımaz, işin planı geçerlidir). */
    const bagKesimli = tikliler.some((x) => isOf(x) !== null && kesimliMi(x));

    const gonder = async (planlar?: Record<string, KesimPlaniGovdesi>) => {
      const kalemler = tikliler.map((x) => {
        const l = uyeLideri(x);
        const j = isOf(x);
        // D-035: onaylı plan işin öncü kaleminde gider (grup üyesi ve süren işe bağlanan plan taşımaz).
        const plan = !j && (l ?? x.no) === x.no ? planlar?.[`g:${x.no}`] : undefined;
        return {
          no: x.no, yeniId: secim[x.no]?.yeniId ?? "",
          // D-050: stok paketi seçimi yalnız salt değişim kaleminde gider (sunucu tamir kaleminde reddeder).
          ...(!repairMi(x) && secim[x.no]?.stokPaketId ? { stokPaketId: secim[x.no]?.stokPaketId } : {}),
          ...(l !== null && l !== x.no ? { grup: l } : {}),
          ...(j ? { tamirId: j.id } : {}),
          ...(plan ? { kesimPlani: plan } : {}),
        };
      });
      if (await dene(() => api.post(`/muayene/${rn}/degisim`, { kod: dk.kod, kalemler }))) setDegisim(null);
    };

    /** "Next: cut plan": her plan işi için pencere girdisi okunur (salt okuma); bu aktarımda işaretli bulgular şemada "bekleyen" çizilmez. */
    const ileri = async () => {
      setIslem(null);
      setKesimYukleniyor(true);
      try {
        const gonderilen = new Set(tikliler.map((x) => x.no));
        const isler = await Promise.all(planIsleri.map(async (p): Promise<KesimIsi> => {
          const y = await api.get<KesimGirdisiYaniti>(`/muayene/${rn}/kesim-girdisi?kod=${encodeURIComponent(dk.kod)}&nolar=${p.nolar.join(",")}`);
          return { anahtar: p.anahtar, yanit: { ...y, bekleyenler: y.bekleyenler.filter((b) => !gonderilen.has(b.no)) } };
        }));
        setKesimIsleri(isler);
        setKesimAdim(2);
      } catch (e) {
        setIslem(e instanceof Error ? e.message : cevir("Error"));
      } finally {
        setKesimYukleniyor(false);
      }
    };

    const kalemAdi = (x: Kalem) => { const st = stOf(x); return `#${x.no} ${x.kalem}${st ? ` (${st.kod})` : ""}`; };

    // D-035 2. adım: aynı pencere (başlık aynı), içerik kesim planı onayı.
    if (kesimAdim === 2 && kesimIsleri.length) {
      const adlar = planIsleri.map((p) => p.nolar.map((n) => `#${n}`).join(" + "));
      const ust = (
        <>
          <div className="mb-1 flex flex-wrap items-center gap-2">
            <Etiket tone="ok">{cevir("1 Select items ✓")}</Etiket>
            <span className="text-xs text-ink-3">·</span>
            <Etiket className="bg-orange text-white">{cevir("2 Cut plan approval")}</Etiket>
          </div>
          <p className="mb-3 text-xs text-ink-3">{cevir("In step 1, ticking an item on a Cutting route (ST-03, ST-04, ST-05, ST-06) turns Save into Next: cut plan.")}</p>
          <p className="mb-1 text-sm text-ink-2">
            {adlar.length === 1
              ? cevir`${adlar[0]} is on a Cutting route, so its cut plan is approved here before it goes to Repair planning. Distances are in mm from the box end. Each approved cut becomes one new weld with its own fit-up form.`
              : cevir`${veIle(adlar)} are on a Cutting route, so their cut plan is approved here before they go to Repair planning. Distances are in mm from the box end. Each approved cut becomes one new weld with its own fit-up form.`}
          </p>
          <p className="mb-3 text-xs text-ink-3">
            {cevir`In this transfer: ${tikliler.map(kalemAdi).join(", ")}.`}
            {tiksizler.length > 0 && <> {cevir`Not ticked in step 1, stays waiting: ${tiksizler.map(kalemAdi).join(", ")}.`}</>}
          </p>
        </>
      );
      return (
        <GenisModal baslik={cevir`Waiting Items — ${dk.ad}`} onKapat={() => setDegisim(null)} kilitli={gonderiliyor}>
          <KesimPlaniAdimi
            isler={kesimIsleri}
            ust={ust}
            onGeri={() => { setIslem(null); setKesimAdim(1); }}
            onIptal={() => setDegisim(null)}
            onOnayla={async (planlar) => { setGonderiliyor(true); try { await gonder(planlar); } finally { setGonderiliyor(false); } }}
            onayMetni={cevir`Approve cut plan and send to Repair planning (${tikliler.length})`}
            mesgul={gonderiliyor}
            hata={islem}
            rework={{ dokumanId: riser.reworkDokumanId, ad: riser.reworkRapor, sayfaAraligi: riser.reworkSayfaAraligi }}
          />
        </GenisModal>
      );
    }

    return (
      <Modal baslik={cevir`Waiting Items — ${dk.ad}`} onKapat={() => setDegisim(null)} genis>
        {/* Hata modalin ICINDE gosterilir: sayfa tepesindeki <Hata> modal tarafindan ortuluyor ve
            kullanici "kaydet calismiyor" saniyordu (2026-09-02). */}
        <Hata mesaj={islem} />
        <p className="mb-3 text-sm text-ink-2">{cevir("Mark the material that has arrived. Unmarked Item remains waiting; partial Replacement is supported. In Replacement only Route cards, closure is the new product's ID, those with repair route go to planning.")}</p>
        <div className="space-y-2">{satirlar}</div>
        {repairVar && (
          <div className="mt-3 rounded-md border border-line bg-surface-2 px-3 py-2 text-sm">
            <b>{tikliRepair.length === 1 ? cevir`${tikliRepair.length} finding ticked` : cevir`${tikliRepair.length} findings ticked`} {yeniIs === 1 ? cevir`→ ${yeniIs} new job` : cevir`→ ${yeniIs} new jobs`}</b>
            {oneriler.map(({ x, o, acik }) => (
              <span key={x.no} className={acik ? "text-warn" : "text-ink-2"}> · {acik ? cevir`proposal ${ciftAdi(x, o)} open` : cevir`proposal ${ciftAdi(x, o)} not accepted`}</span>
            ))}
            {ozetSatiri && <div className="mt-0.5 text-xs text-ink-2">{ozetSatiri}</div>}
          </div>
        )}
        {repairVar && (
          <Band tip="bilgi" className="mt-3">
            <b>{cevir("How same repair works.")}</b>
            <ul className="mt-1 list-disc space-y-0.5 pl-5">
              <li>{cevir("Findings that point to the same repair are sent as one job: one route, one cut plan, one set of welds and fit-up forms. When the job is finished, every finding in it counts as done here.")}</li>
              <li>{cevir("The system proposes; nothing is merged until you choose Send as one job (or Attach to running job). Keep separate sends each finding as its own job; the declined proposal is written to the component log. It recognises the same repair from the rework input (“tied to the route of #N”, “a SINGLE job with #N”, “the same … job as #N”); otherwise from the same component, the same ST and the same rework page references. MRB identities and heat numbers are not used, because several pieces share them.")}</li>
              <li>{cevir("“may be absorbed … HUMAN APPROVAL” only shows a “Possibly same repair” hint; those findings are not grouped unless you join them.")}</li>
              <li>{cevir("Separate on a group row undoes Send as one job. + Same repair as… joins a finding by hand to another waiting item or to a running job of this component.")}</li>
              <li>{cevir("If the findings' STs differ, the job takes the lead finding's route; the other ST can be chosen in the group's route.")}</li>
              <li>{cevir("A finding whose same repair is already running shows Attach to running job: it joins that job, with no new job and no new route, and shows “in Repair planning (with #N)”.")}</li>
              <li>{cevir("Replacement only items (ST-07, ST-08, ST-11) open no job and are not grouped.")}</li>
              <li>{cevir("A finding sent back from Repair planning waits here again with its previous route, which can be changed. It can be sent again as a new job, grouped with another waiting item, or attached to a running job. The cancelled job stays readable, marked “Sent back to Inspection verification”.")}</li>
            </ul>
          </Band>
        )}
        {repairVar && <p className="mt-2 text-xs text-ink-3">{cevir("Grouping and attaching are written to the job's timeline and to this component's log.")}</p>}
        {bekleyenler.some(kesimliMi) && <p className="mt-2 text-xs text-ink-3">{cevir("Items on a Cutting route (ST-03, ST-04, ST-05, ST-06) need a cut plan approved by the Quality Lead before they go to Repair planning.")}</p>}
        <div className="mt-3 flex flex-wrap gap-2">
          {/* D-035: kesim rotalı işaretli iş varsa Save yerine "Next: cut plan" (yalnız kalite lideri; PM ve sistem yöneticisi her zaman). */}
          {planIsleri.length > 0
            ? (
              <Buton tur="birincil" disabled={!tikliler.length || !kesimYetkili} mesgul={kesimYukleniyor} title={kesimYetkili ? undefined : cevir("Cut plan approval: Quality Lead")} onClick={ileri}>
                {cevir("Next: cut plan")}
              </Buton>
            )
            : (
              <Buton tur="birincil" disabled={!tikliler.length || (bagKesimli && !kesimYetkili)} title={bagKesimli && !kesimYetkili ? cevir("Cut plan approval: Quality Lead") : undefined} onClick={() => gonder()}>
                {tekBag ? cevir`Attach #${tekBag.x.no} to job ${isKisa(tekBag.j.id)}` : cevir("Save")}
              </Buton>
            )}
          <Buton onClick={() => setDegisim(null)}>{cevir("Cancel")}</Buton>
        </div>
      </Modal>
    );
  };

  return (
    <Sayfa
      ust={<Link to="/dogrulama" className="normal-case tracking-normal text-orange">{cevir("← Inspection verification")}</Link>}
      baslik={<>R{riser.rn} {cevir("Triage")}</>}
      alt={riser.kaynak ?? undefined}
      aksiyon={<div className="flex flex-wrap gap-2">
        {idariSayisi > 0 && (
          <Buton kucuk onClick={() => api.indir(`/muayene/${rn}/idari-kapanis.xlsx`)} title={cevir("Paste into the rework input excel as the ADMIN_CLOSURES sheet")}>
            {cevir("Download administrative records (")}{idariSayisi})
          </Buton>
        )}
        <Buton kucuk islem="dogrula" tur={riser.reworkIslendi ? "normal" : "birincil"} onClick={() => { setIslem(null); setReworkPdf(null); setReworkSayfaSecimi(BOS_SAYFA); setReworkGecis(null); setReworkSonuc(null); setReworkAcik(true); }}>
          {riser.reworkIslendi ? cevir("Change Rework Scope") : cevir("Upload Rework Scope")}
        </Buton>
        <Buton kucuk islem="dogrula" tur="tehlike" onClick={() => setSifirla(true)}>{cevir("Reset set")}</Buton>
      </div>}
    >
      <Hata mesaj={islem} />
      {/* Rework yüklemesinin sonucu; D-069: "Open as Items" sonucu da buraya yazılır (açılan her kalem için bir satır). */}
      {reworkSonuc && <Band tip="ok">{reworkSonuc.map((n, i) => <div key={i}>{n}</div>)}</Band>}

      {/* Prototipin kural başlığı: sayfanın nasıl okunacağını anlatır, süs değil. */}
      <Band tip="bilgi">
        <b>{cevir("Goal: all disposed Items of each component passing, until the bar turns completely green.")}</b>{" "}
        {cevir("Accept passes · REWORK holds the component (rework analysis Document is expected) · REJECT/REPLACE enters Replacement process · Items without OSI Disposition (\"—\") are ignored · Buoyancy is out of scope. In Replacement:")} <b>{cevir("Waiting for Material")}</b> {cevir("(order date + day counter) ·")} <b>{cevir("Material Replacement")}</b> {cevir("(opt-in marking, new product ID if needed → assembly; unmarked remains waiting; those with repair route go to planning).")}
      </Band>

      <Kutu>
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <span className="text-lg font-semibold tracking-tight">RN {riser.rn}</span>
          {/* 7 nokta: komponent başına renk, tıklanınca o akordeon açılır. */}
          <span className="flex items-center gap-2">
            <span className="text-sm text-ink-2 tnum">{gorunur.filter((k) => k.tamam).length}/{gorunur.length} {cevir("complete")}</span>
            <span className="flex gap-1">{gorunur.map((k) => (
              <button key={k.id} type="button" title={`${k.ad} — ${rozetBilgi(k).metin}`} aria-label={k.ad}
                onClick={() => { setAcik((s) => ({ ...s, [k.kod]: true })); document.getElementById(`komp-${k.kod}`)?.scrollIntoView({ block: "center", behavior: "smooth" }); }}
                className={`h-2.5 w-2.5 rounded-full ring-1 ring-black/10 ${NOKTA[rozetBilgi(k).ton]}`} />
            ))}</span>
          </span>
        </div>
        <div className="grid gap-x-4 gap-y-3 sm:grid-cols-2 lg:grid-cols-4">
          <Kv ad="Assembly SN"><b className="font-mono">{riser.assemblySn ?? "—"}</b></Kv>
          <Kv ad="Assembly PN (MRB)"><span className="font-mono text-xs">{riser.assemblyPnMrb ?? "—"}</span></Kv>
          <Kv ad="Disposition">{riser.dispoImza ?? "—"} · {tarih(riser.dispoTarihi)}{dispoGun !== null && <span className="text-ink-3"> ({dispoGun} {cevir("days)")}</span>}</Kv>
          <Kv ad={cevir("Inspectors")}>{riser.mufettisler ?? "—"}</Kv>
        </div>
        <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-sm text-ink-2">
          {(riser.kaynakMrbIcerik || riser.mrbKaydi) && (
            <span><span className="text-ink-3">{cevir("Part IDs")}</span>{" "}
              {riser.mrbKaydi?.icerikVar
                ? <Link to="/mrb/$id" params={{ id: riser.mrbKaydi.id }} className="font-semibold text-orange">{riser.kaynakMrbIcerik ?? `MRB ${riser.mrbKaydi.sn}`}</Link>
                : <>{riser.kaynakMrbIcerik ?? "—"} <Etiket tone="warn">{cevir("MRB content is not in system")}</Etiket></>}
            </span>
          )}
          {riser.rapor && <span><span className="text-ink-3">{cevir("Report")}</span> {riser.rapor}</span>}
          <span><span className="text-ink-3">{cevir("Input")}</span> {riser.dosya} · {riser.yukleyen} · {tarihSaat(riser.ts)}</span>
          {veri.girdiKaynagi && <span><span className="text-ink-3">{cevir("Disposition input delivered by")}</span> {kaynakMetni(veri.girdiKaynagi)}</span>}
        </div>
        {!riser.survrepDokumanId && <Band tip="not" className="mt-3">{cevir("SURVREP PDF of riser is not in system; page references are not clickable. Connects automatically when uploaded from Field list, the rest of the page is not affected.")}</Band>}
        {riser.reworkIslendi && (
          <Band tip="ok" className="mt-3">
            <b>{cevir("Case 2: Rework Scope processed.")}</b>{" "}
            {[riser.reworkRapor, riser.reworkDogrulamaImza && `verification ${riser.reworkDogrulamaImza} ${tarih(riser.reworkDogrulamaTarih)}`,
              riser.reworkDispoImza && `closure ${riser.reworkDispoImza} ${tarih(riser.reworkDispoTarih)}`].filter(Boolean).join(" · ")}
            {riser.reworkDokumanId && riser.reworkSayfaAraligi && (
              <> <a href={belgeLinki(riser.reworkDokumanId, riser.reworkSayfaAraligi)} target="_blank" rel="noreferrer"><Cip>📄 {cevir("Rework Scope PDF")} · {sayfaEtiketi(riser.reworkSayfaAraligi)}</Cip></a></>
            )}
            {!riser.reworkDokumanId && <div className="mt-1">{cevir("Rework Scope PDF not uploaded; purple rw p.N references are not clickable.")}</div>}
            {veri.reworkGirdiKaynagi && <div className="mt-1">{cevir("Rework input delivered by")} {kaynakMetni(veri.reworkGirdiKaynagi)}</div>}
          </Band>
        )}
        {celiskiler.length > 0 && (
          <Band tip="duvar" className="mt-3">
            <b>{celiskiler.length} {cevir("ID Conflict waiting for human approval.")}</b> {cevir("System does not match these automatically.")}
            <ul className="mt-1 list-disc space-y-0.5 pl-5">{celiskiler.map((c) => <li key={c.id}>{c.mesaj}</li>)}</ul>
          </Band>
        )}
        {/* D-069: kalemi açılmamış REJECT'li ek satır bandı; görünür komponentlerden birinde böyle bir satır oldukça çizilir. Kuyruk cümlesi
            yalnız riser assembly kuyruğundayken çıkar. Düğme onay penceresini açar; işi "dogrula" yetkisi olan yapar. */}
        {ekAcik.length > 0 && (
          <Band tip="duvar" className="mt-3">
            <b>{ekBantBasligi(ekAcik.length)}</b> {ekBantAciklamasi(ekAcik)}
            {ekKuyrukCumlesi && <div className="mt-1">{ekKuyrukCumlesi}</div>}
            <div className="mt-2">
              <Buton kucuk tur="birincil" islem="dogrula" onClick={() => { setIslem(null); setEkAc(true); }}>{cevir("Open as Replacement Items ({0})", ekAcik.length)}</Buton>
            </div>
          </Band>
        )}
        {uyarilar.length > 0 && (
          <Band tip="not" className="mt-3">
            <b>{uyarilar.length} {cevir("note waiting.")}</b> {cevir("Applied data is valid; the following are waiting to be completed.")}
            <ul className="mt-1 list-disc space-y-0.5 pl-5">{uyarilar.map((u) => <li key={u.id}>{u.mesaj}</li>)}</ul>
          </Band>
        )}
      </Kutu>

      {gorunur.map((k) => {
        const gecen = k.kalemler.filter((x) => x.sinif === "gecen");
        const rework = k.kalemler.filter((x) => x.sinif === "rework");
        const degisimK = k.kalemler.filter((x) => x.sinif === "degisim");
        // D-016: henüz muayene edilmemiş kalemler (standartta class pending). D-072: "No record available" yazılanlar (`beyanli`) bu
        // listeye girmez; başlık çipi, grup başlığı ve mavi bant sunucunun `sayim.muayeneBekleyen`iyle aynı sayar.
        const { bekleyen: muayeneBekleyen, beyanli: muayeneBeyanli } = muayeneKalemleri(k.kalemler);
        const bekleyen = degisimK.filter((x) => x.durum === "bekliyor");
        // Bekleyen kalemin ROTASI ne yapılacağını belirler: salt değişim rotasında yeni ürün gelir,
        // repair rotasında (ör. ST-09 yüzey düzeltme) hiçbir malzeme gelmez, iş planlamaya gider.
        // Arka uç bunu zaten ayırıyor (durum "degisti" / "repair"); buton da aynı dili konuşmalı.
        const stBul = (x: Kalem) => stg.find((sg) => sg.kod === (x.rotaSecim ?? x.stOneri));
        const repairBekleyen = bekleyen.filter((x) => ((stBul(x)?.tip ?? x.rotaTipi) ?? "degisim") !== "degisim");
        const degisimBekleyen = bekleyen.filter((x) => ((stBul(x)?.tip ?? x.rotaTipi) ?? "degisim") === "degisim");
        // Sipariş tarihi yalnız malzeme gerektiren rota varken anlamlı; rota seçilmemişse gizlemeyiz.
        const malzemeliBekleyen = bekleyen.filter((x) => { const st = stBul(x); return st ? st.malzeme : true; });
        const aksiyonAdi = bekleyen.length === 0 ? cevir("Material Replacement")
          : repairBekleyen.length && !degisimBekleyen.length ? cevir`Send to Repair planning (${bekleyen.length})`
          : degisimBekleyen.length && !repairBekleyen.length ? cevir`Material Replacement (${bekleyen.length} waiting)`
          : cevir`Process Items (${degisimBekleyen.length} Replacement · ${repairBekleyen.length} repair)`;
        const siparisGun = gunFark(k.siparisTarihi);
        const ac = acik[k.kod] ?? false;
        // D-035: pencereyi saha inspektörü ("dogrula") açar; kesim rotalı kalem bekliyorsa kalite lideri ("kesimPlani") da.
        const kesimliBekleyen = repairBekleyen.some((x) => kesimRotasiMi(stBul(x)));
        const pencereYetkili = Boolean(ben && (yetkiliMi(ben.rol, "dogrula") || (kesimliBekleyen && yetkiliMi(ben.rol, "kesimPlani"))));
        const pencereIpucu = pencereYetkili ? undefined
          : kesimliBekleyen ? cevir("Field Inspector: items without cuts · Quality Lead: items on a Cutting route")
            : yetkiMetni("dogrula", (r) => ROL_ETIKET[r]);
        return (
          <Kutu key={k.id} className={`scroll-mt-20`}>
            <div id={`komp-${k.kod}`} />
            <button type="button" className="w-full text-left" onClick={() => setAcik((s) => ({ ...s, [k.kod]: !ac }))}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="flex flex-wrap items-baseline gap-x-2">
                  <b className="text-base">{k.ad}</b>
                  <span className="text-xs text-ink-3">{k.mrbSatirAdi ?? k.mrbKimlik ?? ""}{k.mrbPn ? ` · ${k.mrbPn}` : ""}</span>
                </span>
                <span className="flex flex-wrap items-center gap-1">
                  {k.siparisTarihi && <Etiket tone="warn">{cevir("order:")} {tarih(k.siparisTarihi)} ({siparisGun} {cevir("d)")}</Etiket>}
                  {k.gecenAktarildi && !k.aktarildi && <Etiket tone="ok">{cevir("Passed items transferred")}</Etiket>}
                  {muayeneBekleyen.length > 0 && <Etiket tone="hold">{cevir`Inspection pending · ${muayeneBekleyen.length} items`}</Etiket>}
                  {rozet(k)}
                  <span className="ml-1 text-xs text-ink-3">{ac ? "hide" : "open"}</span>
                </span>
              </div>
              {bar(k.bar)}
              {/* Prototipin bar açıklaması: hedefi ve bekleme süresini birlikte söyler. */}
              <div className="mt-1 text-xs text-ink-3">
                {k.bar.yesil} {cevir("completed ·")} {k.sayim.reworkAcik} {cevir("rework open ·")} {k.sayim.bekleyen} {cevir("waiting for Replacement")}{ekCubukAlti(k.sayim)}{k.sayim.muayeneBekleyen > 0 ? ` · ${k.sayim.muayeneBekleyen} ${cevir("inspection pending")}` : ""}{kayitYokCubukAlti(k.sayim)}
                {cevir(" · target: ")}{k.bar.toplam}/{k.bar.toplam} {cevir("(bar completely green)")}
                {(k.bar.sari > 0 || k.bar.kirmizi > 0) && !k.tamam && dispoGun !== null && <span className="text-warn"> {cevir("· since Disposition")} {dispoGun} {cevir("days")}</span>}
              </div>
            </button>
            {k.baslikNotu && <div className="mt-2 text-xs text-ink-3">{k.baslikNotu}</div>}

            {ac && (
              <div className="mt-3 space-y-4 border-t border-line pt-3">
                {degisimK.length > 0 && (
                  <div>
                    <h3 className="mb-2 text-sm font-semibold">{cevir("Replacement Items (")}{degisimK.length})</h3>
                    <div className="space-y-3">{degisimK.map((x) => {
                      const st = stg.find((s) => s.kod === (x.rotaSecim ?? x.stOneri));
                      return (
                        <div key={x.id} className="rounded-md border border-line p-3">
                          <div className="flex flex-wrap items-start justify-between gap-2">
                            <div className="min-w-0 flex-1">
                              {kalemGovde(x)}{geriNotu(x, false)}
                              {/* D-041/D-050: değişimde seçilen stok ve paketi; kapanmış kalemde "Change stock package". */}
                              {stokSatiri(k, x)}
                            </div>
                            <span className="flex flex-col items-end gap-1">
                              {/* Karar rozeti kalem başlığında (kararRozeti); burada yalnız süreç durumu kalır,
                                  yoksa "REPLACE" + altında "değişti" gibi çift anlatım oluyordu. */}
                              {x.durum === "degisti" && x.yeniId && <Etiket tone="ok">{cevir("new ID ·")} {x.yeniId}</Etiket>}
                              {x.durum === "repair" && isRozeti(k, x)}
                              {x.durum === "bekliyor" && <Etiket tone="warn">{cevir("waiting")}</Etiket>}
                            </span>
                          </div>
                          <div className="mt-2 flex flex-wrap items-center gap-2 text-sm">
                            <span className="text-ink-3">{cevir("Route card")}</span>
                            <select className={girdi + " max-w-[320px]"} value={x.rotaSecim ?? x.stOneri ?? ""} disabled={x.durum !== "bekliyor"}
                              onChange={(e) => void dene(() => api.post(`/muayene/${rn}/rota`, { kod: k.kod, no: x.no, st: e.target.value || null }))}>
                              <option value="">{cevir("not selected")}</option>
                              {stSecenekleri(x.rotaSecim ?? x.stOneri)}
                            </select>
                            {st && <Etiket>{st.rotaKod}</Etiket>}
                            {st?.malzeme && <Etiket tone="warn">{cevir("requires material")}</Etiket>}
                            {st && <Etiket tone={st.tip === "degisim" ? "info" : "neutral"}>{st.tip === "degisim" ? cevir("Replacement only") : cevir("repair")}</Etiket>}
                            {x.siparisPn && <span className="text-xs text-ink-3">{cevir("order PN")} <b className="font-mono">{x.siparisPn}</b></span>}
                          </div>
                        </div>
                      );
                    })}</div>
                  </div>
                )}

                {rework.length > 0 && (
                  <div>
                    <h3 className="mb-2 text-sm font-semibold">{cevir("Rework Items (")}{rework.length}){k.sayim.reworkKapanan > 0 ? ` · ${k.sayim.reworkKapanan} closed` : ""}</h3>
                    <div className="space-y-2">{rework.map((x) => {
                      const kapali = Boolean(x.kapanis) || x.idariTip === "kanit" || x.idariTip === "kabul";
                      return (
                        <div key={x.id} className={`rounded-md border p-3 ${x.kapanis ? "border-ok/40" : x.idariTip ? "border-hold/40" : "border-line opacity-80"}`}>
                          <div className="flex flex-wrap items-start justify-between gap-2">
                            <div className="min-w-0 flex-1">{kalemGovde(x)}</div>
                            <span className="flex flex-col items-end gap-1">
                              {x.kapanis && <Etiket tone="ok">{cevir("REWORK → CLOSED")}</Etiket>}
                              {!x.kapanis && x.idariTip && x.idariTip !== "osi-iade" && <Etiket tone="hold">{cevir("ADMINISTRATIVE CLOSURE")}</Etiket>}
                              {!kapali && !x.idariTip && <Etiket tone="warn">{cevir("open")}</Etiket>}
                              {/* İdari aksiyon yalnız yöneticide ve yalnız açık konularda anlamlı. */}
                              {ben?.yonetici && !x.kapanis && (
                                <Buton kucuk onClick={() => { setIdari({ kod: k.kod, no: x.no, kalem: x.kalem, mevcut: x.idariTip }); setIdariTip(idariIlkTip(x.idariTip)); setIdariGerekce(""); setIdariKanit(""); setIdariSt(""); }}>
                                  {x.idariTip ? cevir("Edit administrative record") : cevir("Administrative action...")}
                                </Buton>
                              )}
                            </span>
                          </div>
                          {/* Girdi excel'inin ST (K) ve "rota tipi" (L) kolonları rework kalemlerinde de
                              gösterilir. Önceden yalnız değişim bloğunda çiziliyordu, bu yüzden excel'deki
                              rota değeri ekrana hiç çıkmıyordu. Salt okunur: route SEÇİMİ değişim işidir. */}
                          {(x.stOneri || x.rotaTipi) && (
                            <div className="mt-2 flex flex-wrap items-center gap-2 text-sm">
                              <span className="text-ink-3">{cevir("Route card")}</span>
                              {(() => { const st = stg.find((s) => s.kod === x.stOneri); return (<>
                                <Etiket>{x.stOneri ?? cevir("No ST")}{st ? ` — ${st.ad}` : ""}</Etiket>
                                {st && <Etiket>{st.rotaKod}</Etiket>}
                                {st?.malzeme && <Etiket tone="warn">{cevir("requires material")}</Etiket>}
                                {x.rotaTipi && <span className="text-xs text-ink-3">{cevir("type:")} {x.rotaTipi}</span>}
                                {!st && x.stOneri && <span className="text-xs text-ink-3">{cevir("Not in ST Register (route card undefined)")}</span>}
                              </>); })()}
                            </div>
                          )}
                        </div>
                      );
                    })}</div>
                    {k.sayim.reworkAcik > 0
                      ? <Band tip="not" className="mt-2">{k.sayim.reworkAcik} {cevir("rework Item is open; component cannot be transferred before closing. Administrative action can be taken for an issue that cannot be closed by Report chain.")}</Band>
                      : <Band tip="ok" className="mt-2">{cevir("All rework issues are closed.")}</Band>}
                  </div>
                )}

                {muayeneBekleyen.length > 0 && (
                  <div>
                    <h3 className="mb-2 text-sm font-semibold">{cevir("Inspection pending (")}{muayeneBekleyen.length})</h3>
                    <div className="space-y-2">{muayeneBekleyen.map((x) => (
                      <div key={x.id} className="rounded-md border border-hold/40 p-3">
                        <div className="flex flex-wrap items-start justify-between gap-2">
                          <div className="min-w-0 flex-1">{kalemGovde(x)}</div>
                          {/* D-072: çipin altında "No record available…" (yetki kayitYokKapi; öteki rollerde pasif ve ipuçlu). */}
                          <BekleyenKalemSagi onKayitYok={() => { setIslem(null); setKayitYok({ kod: k.kod, komponent: k.ad, no: x.no, kalem: x.kalem, gerekce: "", kanit: "", mesgul: false }); }} />
                        </div>
                      </div>
                    ))}</div>
                    <Band tip="bilgi" className="mt-2">{muayeneBekleyen.length} {cevir("items have not been inspected yet. The component cannot be completed or transferred to assembly until the follow-up inspection's disposition is uploaded; the new disposition input replaces this one.")} {kayitYokBantEki()}</Band>
                  </div>
                )}

                {/* D-072: "No record available" yazılmış muayene bekleyen kalemler; "Inspection pending" grubunun hemen ardından (bekleyen
                    kalmayınca onun yerinde). Kart çerçevesi düz; gövdenin altında gri damga (okunur damgası yoksa çizilmez). */}
                {muayeneBeyanli.length > 0 && (
                  <div>
                    <h3 className="mb-2 text-sm font-semibold">{kayitYokGrupBasligi(muayeneBeyanli.length)}</h3>
                    <p className="mb-2 text-xs text-ink-3">{kayitYokGrupNotu()}</p>
                    <div className="space-y-2">{muayeneBeyanli.map((x) => (
                      <div key={x.id} className="rounded-md border border-line p-3">
                        <div className="flex flex-wrap items-start justify-between gap-2">
                          <div className="min-w-0 flex-1">{kalemGovde(x)}<KayitYokDamgasiKutusu damga={kayitYokAktif(x)} /></div>
                          <BeyanliKalemSagi onGeriAl={() => { setIslem(null); setKayitYokGeri({ kod: k.kod, no: x.no, mesgul: false }); }} />
                        </div>
                      </div>
                    ))}</div>
                  </div>
                )}

                {k.ekDogrulamalar.length > 0 && (
                  <div>
                    <h3 className="mb-2 text-sm font-semibold">{cevir("Rework Report additional verifications (")}{k.ekDogrulamalar.length})</h3>
                    <p className="mb-2 text-xs text-ink-3">{cevir("Records of the rework report that have no Item in the first inspection. They are not Items and count as green for the bar, except REJECT rows: a REJECT row is opened as a Replacement Item and is worked there.")}</p>
                    {/* D-069: satır kanıt olarak yerinde kalır. Result çipinin rengi metne göredir (sabit yeşil değil); REJECT satırı kalemi
                        açıldıysa gri "handled as #N under Replacement Items", açılmadıysa kırmızı "not opened as an Item yet" çipini taşır. */}
                    <div className="space-y-2">{k.ekDogrulamalar.map((e) => {
                      const cip = ekSatirCipi(e, k.kalemler);
                      return (
                        <div key={e.id} className="rounded-md border border-line p-2 text-sm">
                          <div className="flex flex-wrap items-baseline gap-x-2">
                            {e.item && <span className="text-xs text-ink-3">{cevir("Item")} {e.item}</span>}
                            <b>{e.ad}</b>
                            {e.sonuc && <Etiket tone={ekSonucTonu(e.sonuc)}>{e.sonuc}</Etiket>}
                            {cip && <Etiket tone={cip.ton}>{cip.metin}</Etiket>}
                            {refler(e.rworkRefleri ?? [], riser.reworkDokumanId, riser.reworkRapor, true, riser.reworkSayfaAraligi)}
                          </div>
                          {e.kanit && <div className="mt-0.5 text-xs text-ink-2">{e.kanit}</div>}
                        </div>
                      );
                    })}</div>
                  </div>
                )}

                {gecen.length > 0 && (
                  <details className="group">
                    <summary className="mb-2 cursor-pointer list-none text-sm font-semibold text-ink-2 hover:text-ink">
                      <span className="inline-block w-4 transition group-open:rotate-90">▶</span>
                      {cevir("Passed items (")}{gecen.length}){k.gecenAktarildi ? cevir(" · transferred to assembly") : ""}
                    </summary>
                    <div className="scroll-thin overflow-x-auto">
                      <table className="w-full min-w-[620px]">
                        <thead><tr className="border-b border-line"><th className={tabloBas}>#</th><th className={tabloBas}>{cevir("Item")}</th><th className={tabloBas}>SN / ID</th><th className={tabloBas}>{cevir("MRB equivalent")}</th><th className={tabloBas}>{cevir("Page")}</th></tr></thead>
                        <tbody>{gecen.map((x) => (
                          <tr key={x.id} className="border-b border-line last:border-0 align-top">
                            <td className={tabloHucre}><span className="tnum text-xs text-ink-3">{x.no}</span></td>
                            <td className={tabloHucre}>{x.kalem}</td>
                            <td className={tabloHucre}><span className="font-mono text-xs">{x.snid ?? "—"}</span></td>
                            <td className={tabloHucre}>{kimlikler(x) ?? <span className="text-xs text-ink-3">—</span>}</td>
                            <td className={tabloHucre}>{sref(x) ?? <span className="text-xs text-ink-3">—</span>}</td>
                          </tr>
                        ))}</tbody>
                      </table>
                    </div>
                  </details>
                )}

                {/* D-069: düğmelerin üstünde. Kırmızı: kalemi açılmamış REJECT satırı ("Transfer to assembly" bu yüzden çizilmez; satır riser
                    kutusundaki düğmeyle açılır). Sarı: aktarılmış komponentte açılan kalem bekliyor; aktarım ve kayıtlar durur. */}
                {k.sayim.ekReject > 0 && <Band tip="duvar">{ekAcilmamisCumlesi(k.sayim.ekReject)}</Band>}
                {k.aktarildi && k.sayim.ekBekleyen > 0 && <Band tip="not">{ekYenidenAcikCumlesi(k.sayim.ekBekleyen)}</Band>}
                {/* D-072: aktarılmış komponentte beyansız muayene bekleyen kalem (beyan geri alındı); aktarım ve kayıtlar durur. */}
                {k.aktarildi && k.sayim.muayeneBekleyen > 0 && <Band tip="not">{muayeneYenidenAcikCumlesi(k.sayim.muayeneBekleyen)}</Band>}

                <div className="flex flex-wrap gap-2">
                  {degisimK.length > 0 && (
                    <>
                      {(malzemeliBekleyen.length > 0 || k.siparisTarihi) && (
                        <Buton kucuk islem="dogrula" title={malzemeliBekleyen.length ? undefined : cevir("Route card of waiting Items does not require material")}
                          onClick={() => { setSiparisTarih(k.siparisTarihi ? String(k.siparisTarihi).slice(0, 10) : ""); setSiparis(k); }}>
                          {k.siparisTarihi ? cevir("Update order date") : cevir("Awaiting material")}
                        </Buton>
                      )}
                      <Buton kucuk tur="birincil" disabled={!bekleyen.length || !pencereYetkili} title={pencereIpucu}
                        onClick={() => {
                          setSecim(Object.fromEntries(bekleyen.map((x) => [x.no, { isaretli: false, yeniId: "", stokPaketId: "" }])));
                          // D-076: "Show all stock packages" kutusu kaydedilmez; pencere her açılışta işaretsiz gelir.
                          setTumPaketler({});
                          // D-042: açılışta hiçbir bulgu gruplanmaz ya da işe bağlanmaz; öneri kişinin cevabını bekler.
                          setBirlesim({});
                          setOneriCevap({});
                          // D-035: pencere her açılışta 1. adımdan başlar, önceki kesim planı taslağı tutulmaz.
                          setKesimAdim(1);
                          setKesimIsleri([]);
                          setIslem(null);
                          setDegisim(k);
                        }}>
                        {aksiyonAdi}
                      </Buton>
                    </>
                  )}
                  {k.kod === "fst" && gecen.length > 0 && !k.gecenAktarildi && (
                    <Buton kucuk islem="dogrula" onClick={() => void dene(() => api.post(`/muayene/${rn}/aktar`, { kod: k.kod, kapsam: "gecen" }))}>{cevir("Passed")} {gecen.length} {cevir("transfer Item to assembly")}</Buton>
                  )}
                  {k.aktarilabilir && (
                    <Buton kucuk islem="dogrula" tur="birincil" onClick={() => void dene(() => api.post(`/muayene/${rn}/aktar`, { kod: k.kod, kapsam: "komponent" }))}>{cevir("Transfer to assembly")}</Buton>
                  )}
                </div>

                {k.loglar.length > 0 && (
                  <div className="border-l-2 border-line-strong pl-3 text-sm text-ink-2">
                    {/* Sunucu log satırı İngilizce yazılır; Türkçe kipte tr-elle.tsv kalıplarıyla çevrilir (Komuta olay akışı gibi). */}
                    {k.loglar.map((l) => <div key={l.id}>{tarihSaat(l.ts)} · {l.kim} · {cevir(l.mesaj)}</div>)}
                  </div>
                )}
              </div>
            )}
          </Kutu>
        );
      })}

      {siparis && (
        <Modal baslik={cevir`Awaiting material — ${siparis.ad}`} onKapat={() => setSiparis(null)}>
          <Hata mesaj={islem} />
          <p className="mb-2 text-sm text-ink-2">{cevir("Order")} <b>{cevir("creation")}</b> {cevir("date. Day counter runs from here; waiting time and bottleneck analysis are read from this record. No automatic email is sent, you send the email yourself.")}</p>
          <input type="date" className={girdi} value={siparisTarih} onChange={(e) => setSiparisTarih(e.target.value)} />
          {!siparisTarih && <p className="mt-1 text-sm text-bad">{cevir("Cannot be saved without selecting a date.")}</p>}
          <div className="mt-3 flex gap-2">
            <Buton tur="birincil" disabled={!siparisTarih} onClick={async () => { if (await dene(() => api.post(`/muayene/${rn}/siparis`, { kod: siparis.kod, tarih: siparisTarih }))) setSiparis(null); }}>{cevir("Save")}</Buton>
            <Buton onClick={() => setSiparis(null)}>{cevir("Cancel")}</Buton>
          </div>
        </Modal>
      )}

      {degisim && bekleyenPenceresi(degisim)}

      {/* D-069: riser kutusundaki "Open as Replacement Items (n)" düğmesinin onay penceresi. Satır başına bir liste satırı (komponent ·
          Item no · Name); sarı kutudaki cümleler koşulludur (komponent aktarılmışsa; riser assembly kuyruğundaysa). "Cancel" hiçbir şey yazmaz. */}
      {ekAc && (
        <Modal baslik={cevir("Open additional REJECT rows as Items — R{0}", riser.rn)} onKapat={() => setEkAc(false)} kilitli={ekAciliyor}>
          <Hata mesaj={islem} />
          <p className="mb-3 text-sm text-ink-2">{cevir("Each row below becomes a waiting Replacement Item of its component. Nothing recorded so far is changed: transfers, closed Items, jobs, cut plans and documents stay as they are.")}</p>
          <ul className="mb-3 list-disc space-y-0.5 pl-5 text-sm">{ekAcik.map((s, i) => <li key={i}>{ekSatirAdi(s, true)}</li>)}</ul>
          {ekPencereNotlari.length > 0 && (
            <Band tip="not" className="mb-3">{ekPencereNotlari.map((c, i) => <div key={i} className={i ? "mt-1" : undefined}>{c}</div>)}</Band>
          )}
          <div className="flex flex-wrap gap-2">
            <Buton tur="birincil" islem="dogrula" mesgul={ekAciliyor} disabled={!ekAcik.length} onClick={ekRejectAc}>{cevir("Open as Items")}</Buton>
            <Buton disabled={ekAciliyor} onClick={() => setEkAc(false)}>{cevir("Cancel")}</Buton>
          </div>
        </Modal>
      )}

      {/* D-072: muayene bekleyen kartındaki "No record available…" penceresi; gerekçe zorunlu, kanıt referansı isteğe bağlı. "Cancel" hiçbir şey yazmaz. */}
      {kayitYok && (
        <KayitYokPenceresi kalem={kayitYok} komponent={kayitYok.komponent} gerekce={kayitYok.gerekce} kanit={kayitYok.kanit} mesgul={kayitYok.mesgul} hata={islem}
          onGerekce={(v) => setKayitYok((s) => s && { ...s, gerekce: v })} onKanit={(v) => setKayitYok((s) => s && { ...s, kanit: v })}
          onKaydet={kayitYokKaydet} onKapat={() => setKayitYok(null)} />
      )}
      {/* D-072: beyanlı kartın "Take back…" penceresi. Sarı kutu: komponent aktarılmışsa; komponent tamamsa ve riser beyandan sonra kuyruğa
          girmişse kuyruk cümlesi (sunucunun koşulu). */}
      {kayitYokGeri && geriKomponent && geriKalem && (() => {
        const damga = kayitYokAktif(geriKalem);
        return (
          <KayitYokGeriPenceresi kalem={geriKalem} damga={damga} notlar={kayitYokGeriNotlari(riser.rn, geriKomponent, veri.kuyruk, damga)} mesgul={kayitYokGeri.mesgul} hata={islem}
            onGeriAl={kayitYokGeriAl} onKapat={() => setKayitYokGeri(null)} />
        );
      })()}

      {reworkAcik && (
        <Modal baslik={cevir`Upload Rework Scope — R${riser.rn}`} onKapat={() => setReworkAcik(false)} kilitli={reworkYukleniyor}>
          {/* Sunucunun sayfa denetimi (D-034) gibi hatalar modalin içinde görünsün; sayfa tepesindeki <Hata> örtülü kalır. */}
          <Hata mesaj={islem} />
          <p className="mb-3 text-sm text-ink-2">{cevir("Rework Scope SURVREP and")} <b>{cevir("rework input excel")}</b> (R{riser.rn}{cevir("-rework-input-EN.xlsx, 9 sheets) are uploaded together. Excel is mandatory, processed even without PDF but purple")} <b>{cevir("rw p.N")}</b> {cevir("references remain unclickable.")} <b>{cevir("This upload does not delete decisions made")}</b>{cevir(", overwrites them.")}</p>
          <label className={etiketSinifi}>{cevir("Rework Scope PDF")}</label>
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <Buton kucuk disabled={reworkYukleniyor} onClick={async () => { const f = await dosyaSec(".pdf"); if (f) setReworkPdf(f); }}>{cevir("Select PDF")}</Buton>
            {reworkPdf ? <Cip>{reworkPdf.name}</Cip> : <span className="text-sm text-ink-3">{cevir("not selected")}</span>}
          </div>
          {reworkPdf && <div className="mb-3"><SayfaAlani deger={reworkSayfaSecimi} onChange={setReworkSayfaSecimi} disabled={reworkYukleniyor} /></div>}
          <label className={etiketSinifi}>{cevir("Rework input excel, 9 sheets (mandatory)")}</label>
          <div className="flex flex-wrap items-center gap-2">
            <Buton kucuk disabled={reworkYukleniyor} onClick={async () => { const f = await dosyaSec(".xlsx"); if (f) setReworkGecis(f); }}>{cevir("Select Excel")}</Buton>
            {reworkGecis ? <Cip>{reworkGecis.name}</Cip> : <span className="text-sm text-ink-3">{cevir("not selected")}</span>}
          </div>
          {reworkYukleniyor && <p className="mt-3 text-sm text-ink-3">{cevir("Transition being applied; do not close window.")}</p>}
          <div className="mt-4 flex gap-2">
            <Buton tur="birincil" disabled={!reworkGecis || (reworkPdf !== null && !sayfaGecerli(reworkSayfaSecimi))} mesgul={reworkYukleniyor} onClick={async () => {
              if (!reworkGecis) return;
              setIslem(null); setReworkSonuc(null); setReworkYukleniyor(true);
              try {
                const r = await dosyaYukle<{ ozet: { kapanan: number; dogrulanmayan: number; devir: number; ek: number; teyit: number }; uyarilar: string[] }>(
                  `/muayene/${rn}/rework`, reworkGecis, reworkPdf ? sayfaAlanlari(reworkSayfaSecimi) : {}, reworkPdf ? { dosya: reworkPdf } : {}, "gecis");
                // D-069: sunucunun notları gösterim anında çevrilir ("n ADDITIONAL REJECT row opens as a Replacement Item …" notunun kalıbı sözlüktedir).
                setReworkSonuc([cevir`${r.ozet.kapanan} closure · ${r.ozet.devir} carry over · ${r.ozet.dogrulanmayan} unverified · ${r.ozet.ek} additional verification · ${r.ozet.teyit} confirmation processed.`, ...r.uyarilar.map((u) => cevir(u))]);
                setReworkAcik(false);
                await yenile();
              } catch (e) {
                setIslem(e instanceof Error ? e.message : cevir("Upload failed"));
              } finally { setReworkYukleniyor(false); }
            }}>{reworkYukleniyor ? cevir("Applying...") : cevir("Upload")}</Buton>
            <Buton disabled={reworkYukleniyor} onClick={() => setReworkAcik(false)}>{cevir("Cancel")}</Buton>
          </div>
        </Modal>
      )}

      {idari && (
        <Modal baslik={cevir`Administrative action — #${idari.no} ${idari.kalem}`} onKapat={() => setIdari(null)}>
          <p className="mb-3 text-sm text-ink-2">{cevir("For issue that cannot be closed by Report chain. Administrative record")} <b>{cevir("does not appear as OSI closure")}</b>{cevir(", carries a separate Stamp and falls into log. Reason is mandatory in all types.")}</p>
          <label className={etiketSinifi}>{cevir("Action")}</label>
          <select className={girdi} value={idariTip} onChange={(e) => setIdariTip(e.target.value)}>
            <option value="">{cevir("Select...")}</option>
            <option value="osi-iade">{cevir("Return to OSI — additional verification request (not closure)")}</option>
            <option value="kanit">{cevir("Close with evidence (Accept)")}</option>
            <option value="kabul">{cevir("Accept as-is")}</option>
            <option value="reject">{cevir("Convert to Reject — move to Replacement process")}</option>
          </select>
          <label className={etiketSinifi + " mt-3"}>{cevir("Reason (mandatory)")}</label>
          <textarea className={alan} rows={2} value={idariGerekce} onChange={(e) => setIdariGerekce(e.target.value)} />
          {idariTip === "kanit" && (<>
            <label className={etiketSinifi + " mt-3"}>{cevir("Evidence reference (mandatory)")}</label>
            <input className={girdi} placeholder={cevir("photo, email or document no")} value={idariKanit} onChange={(e) => setIdariKanit(e.target.value)} />
          </>)}
          {idariTip === "reject" && (<>
            <label className={etiketSinifi + " mt-3"}>{cevir("Route card")}</label>
            <select className={girdi} value={idariSt} onChange={(e) => setIdariSt(e.target.value)}>
              <option value="">{cevir("to be selected later")}</option>
              {stSecenekleri(idariSt)}
            </select>
          </>)}
          <div className="mt-4 flex flex-wrap gap-2">
            <Buton tur="birincil" disabled={!idariKaydedilebilir(idariTip, idariGerekce, idariKanit)} title={idariSaveIpucu(idariTip)}
              onClick={async () => { if (await dene(() => api.post(`/muayene/${rn}/idari-kapanis`, { kod: idari.kod, no: idari.no, tip: idariTip, gerekce: idariGerekce, kanit: idariKanit, st: idariSt || undefined }))) setIdari(null); }}>{cevir("Save")}</Buton>
            {idari.mevcut && (
              <Buton tur="tehlike" onClick={async () => { if (await dene(() => api.sil(`/muayene/${rn}/idari-kapanis/${idari.no}?kod=${encodeURIComponent(idari.kod)}`))) setIdari(null); }}>{cevir("Undo administrative record")}</Buton>
            )}
            <Buton onClick={() => setIdari(null)}>{cevir("Cancel")}</Buton>
          </div>
        </Modal>
      )}

      {sifirla && (
        <Modal baslik={cevir`Reset set — R${riser.rn}`} onKapat={() => setSifirla(false)} kilitli={sifirlaniyor}>
          <p className="text-base text-ink-2">{cevir("Item level Triage of this riser will be completely deleted:")} {gorunur.length} {cevir("component,")} {toplamKalem} {cevir("Item and all decisions made (route selections, order dates, Replacements, transfers).")}</p>
          {/* U-040: bulgusu Repair planning'deki bir işte olan set sıfırlanmaz (sunucu da reddeder); işler burada listelenir. */}
          {sifirlamaIsleri.length > 0 && (
            <Band tip="duvar" className="mt-2">
              <div>{cevir("This set cannot be reset while its findings are in Repair planning: the jobs would be left without their findings and sending again would open a second job for the same repair. Send the findings back to Inspection verification first (on the job's component page), then reset.")}</div>
              <ul className="mt-1 list-disc pl-5">
                {sifirlamaIsleri.map((j) => (
                  <li key={j.id}>
                    <Link to="/komponent/$id" params={{ id: j.id }} className="font-medium underline hover:opacity-80">{cevir`job ${isKisa(j.id)}`}</Link>
                    {" · "}{bulguMetni(j)} · {adimMetni(j)}
                  </li>
                ))}
              </ul>
            </Band>
          )}
          {toplamLog > 0 && <Band tip="duvar" className="mt-2"><b>{toplamLog} {cevir("log row")}</b> {cevir("exists, meaning decisions were made in this set. They will be archived in the audit log before deletion; history is not lost but screen status starts from scratch.")}</Band>}
          <Band tip="bilgi" className="mt-2">{cevir("Do this only if input excel will be corrected and re-uploaded. After resetting, you can reload the file from this screen or from Disposition step in Field list.")}</Band>
          <div className="mt-3 flex gap-2">
            <Buton tur="tehlike" mesgul={sifirlaniyor} disabled={sifirlamaIsleri.length > 0} title={sifirlamaIsleri.length > 0 ? cevir("Send the findings in Repair planning back to Inspection verification first.") : undefined} onClick={async () => {
              setIslem(null);
              setSifirlaniyor(true);
              try {
                await api.post(`/muayene/${rn}/sifirla`);
                void nav({ to: "/dogrulama" });
              } catch (e) {
                setIslem(e instanceof Error ? e.message : cevir("Could not be reset"));
                setSifirla(false);
              } finally {
                setSifirlaniyor(false);
              }
            }}>{sifirlaniyor ? cevir("Deleting…") : cevir("Yes, delete set")}</Buton>
            <Buton disabled={sifirlaniyor} onClick={() => setSifirla(false)}>{cevir("Cancel")}</Buton>
          </div>
        </Modal>
      )}

      {pdf && (
        <Suspense fallback={<div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/70 text-sm text-white">{cevir("Loading viewer...")}</div>}>
          <SurvrepGoruntuleyici dokumanId={pdf.dokumanId} ad={pdf.ad} sayfa={pdf.sayfa} onKapat={() => setPdf(null)} />
        </Suspense>
      )}
    </Sayfa>
  );
}
